// src/pages/DashboardPage.js
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import pb from '../lib/pocketbase';
import confetti from 'canvas-confetti';

import FloatingSidebar from '../components/client/FloatingSidebar';
import ClientTabs from '../components/client/ClientTabs';
import PDFGenerator from '../utils/PDFGenerator';
import ClientHelpBar from '../components/common/ClientHelpBar';
import LiveSiteViewer from '../components/realisations/LiveSiteViewer';
import ClientProjectCard from '../components/client/ClientProjectCard';
import ClientProfileForm from '../components/client/ClientProfileForm';
import ClientProjectSpecifications from '../components/client/ClientProjectSpecifications';

const getDayLabel = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Aujourd\'hui';
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};
const footerLinkStyle = {
  textDecoration: 'none',
  color: 'var(--muted, #64748b)',
  fontSize: '0.95rem',
  fontWeight: 500,
  transition: 'color 0.2s',
  cursor: 'pointer'
};

const DashboardFooter = ({ profile, showToast }) => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [selectedEmoji, setSelectedEmoji] = React.useState(null);

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Si déjà installé ou déjà détecté (certains navigateurs gardent l'event)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setDeferredPrompt(null);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  return (
    <footer className="dashboard-footer" style={{
      width: '100%',
      margin: 0,
      marginTop: 0,
      padding: '60px 0 30px',
      borderTop: '1px solid var(--border, #e5e7eb)',
      background: 'var(--surface, #ffffff)',
      boxShadow: '0 -10px 40px rgba(0,0,0,0.02)',
      position: 'relative',
      zIndex: 10
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        {/* Main Links Grid */}
        <div className="footer-links-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 40,
          marginBottom: deferredPrompt ? 60 : 30
        }}>
          <div className="footer-col">
            <h5 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary, #1e293b)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 20 }}>Espace Client</h5>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Link to="/mon-compte" style={footerLinkStyle}>Tableau de bord</Link>
              <Link to="/mon-compte/projets" style={footerLinkStyle}>Mes Projets</Link>
              <Link to="/mon-compte/devis" style={footerLinkStyle}>Mes Devis</Link>
              <Link to="/mon-compte/factures" style={footerLinkStyle}>Mes Factures</Link>
            </nav>
          </div>
          <div className="footer-col">
            <h5 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary, #1e293b)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 20 }}>Support & Aide</h5>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Link to="/contact" style={footerLinkStyle}>Contactez-nous</Link>
              <Link to="/faq" style={footerLinkStyle}>Centre d'aide</Link>
              <Link to="/mon-compte/messages" style={footerLinkStyle}>Parler à un conseiller</Link>
              <Link to="/onboarding" style={footerLinkStyle}>Nouveau projet</Link>
            </nav>
          </div>
          <div className="footer-col" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Round Feedback Icons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary, #1e293b)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Votre avis nous intéresse</span>
              <div style={{ display: 'flex', gap: 10 }}>
                {['😞', '😐', '😊', '😁', '😍'].map((emoji, i) => (
                  <button
                    key={i}
                    className={`hover-scale feedback-btn ${selectedEmoji === i ? 'active' : ''}`}
                    style={{
                      width: 44, height: 44,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: selectedEmoji === i ? 'white' : 'var(--surface-alt, #f8fafc)',
                      border: selectedEmoji === i ? '2px solid #6366f1' : '1px solid var(--border, #e2e8f0)',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      fontSize: '1.4rem',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: selectedEmoji === i ? '0 4px 12px rgba(99, 102, 241, 0.2)' : '0 2px 5px rgba(0,0,0,0.02)',
                      filter: selectedEmoji === i ? 'grayscale(0%) opacity(1)' : 'grayscale(100%) opacity(0.6)',
                      transform: selectedEmoji === i ? 'scale(1.1) translateY(-2px)' : 'none'
                    }}
                    onClick={() => {
                      setSelectedEmoji(i);
                      confetti({
                        particleCount: 100,
                        spread: 70,
                        origin: { y: 0.8 },
                        colors: ['#6366f1', '#4f46e5', '#10b981']
                      });
                      if (showToast) showToast("Merci pour votre retour précieux ! ❤️", "success");
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <style>{`
                .feedback-btn:hover {
                  filter: grayscale(0%) opacity(1) !important;
                  transform: scale(1.15) translateY(-5px) !important;
                  border-color: #6366f1 !important;
                  background: white !important;
                }
                .feedback-btn.active {
                  filter: grayscale(0%) opacity(1) !important;
                }
              `}</style>
            </div>


            {/* Profile Completion */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Round Completion */}
              <div style={{ position: 'relative', width: 44, height: 44 }} title="Complétion du profil">
                <svg width="44" height="44" viewBox="0 0 44 44">
                  <circle cx="22" cy="22" r="18" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                  <circle cx="22" cy="22" r="18" fill="none" stroke="#4f46e5" strokeWidth="4"
                    strokeDasharray={2 * Math.PI * 18}
                    strokeDashoffset={2 * Math.PI * 18 * (1 - (Math.round((Object.values(profile || {}).filter(Boolean).length / 7) * 100) / 100))}
                    strokeLinecap="round" transform="rotate(-90 22 22)" />
                </svg>
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800 }}>
                  {Math.round((Object.values(profile || {}).filter(Boolean).length / 7) * 100)}%
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary, #1e293b)' }}>Votre Profil</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--muted, #64748b)', fontWeight: 500 }}>Complétion des informations</span>
              </div>
            </div>
          </div>
        </div>

        {/* Installation CTA Section (Conditionally displayed) */}
        {deferredPrompt && (
          <div className="install-banner" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 32px',
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            borderRadius: 24,
            color: 'white',
            marginBottom: 60,
            boxShadow: '0 20px 40px -10px rgba(79, 70, 229, 0.3)',
            flexWrap: 'wrap',
            gap: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{
                fontSize: '2.5rem',
                background: 'rgba(255,255,255,0.2)',
                width: 64, height: 64,
                borderRadius: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}>📱</div>
              <div>
                <h4 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>InfoColab toujours avec vous</h4>
                <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: '0.95rem' }}>Installez l'application sur votre écran d'accueil pour un accès instantané.</p>
              </div>
            </div>
            <button
              onClick={handleInstallClick}
              style={{
                background: 'white',
                color: '#4f46e5',
                border: 'none',
                padding: '12px 24px',
                borderRadius: 12,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s',
              }} className="hover-scale">Installer l'application</button>
          </div>
        )}

        {/* Bottom Line */}
        <div style={{
          paddingTop: 30,
          borderTop: '1px solid var(--border, #e2e8f0)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 20,
          fontSize: '0.85rem',
          color: 'var(--muted, #94a3b8)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <p style={{ margin: 0 }}>© {new Date().getFullYear()} InfoColab. Votre succès, notre priorité.</p>
            {/* Live Status Pill */}
            <div style={{
              padding: '6px 14px',
              background: 'rgba(16, 185, 129, 0.05)',
              borderRadius: 30,
              border: '1px solid rgba(16, 185, 129, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '0.75rem',
              color: '#059669',
              fontWeight: 700
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.2)' }} />
              Systèmes opérationnels
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <Link to="/cgu" style={footerLinkStyle}>Mentions légales</Link>
            <Link to="/confidentialite" style={footerLinkStyle}>Confidentialité</Link>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="hover-scale"
              style={{
                width: 32, height: 32, borderRadius: '50%', border: '1.5px solid #e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'none', cursor: 'pointer', color: 'var(--muted, #64748b)',
                transition: 'all 0.2s',
                marginLeft: 10
              }}
              title="Retour en haut"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6" /></svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = React.useState(null);
  const [pendingQuotes, setPendingQuotes] = React.useState(0);
  const [unpaidInvoices, setUnpaidInvoices] = React.useState(0);
  const [pendingProjects, setPendingProjects] = React.useState(0);
  const [pendingMeetings, setPendingMeetings] = React.useState(0);
  const [unreadMessages, setUnreadMessages] = React.useState(0);
  const [signedContractsCount, setSignedContractsCount] = React.useState(0);

  // --- Premium UI: Toast System ---
  const [toast, setToast] = React.useState({ show: false, message: '', type: 'info' });
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 4000);
  };
  const [meetings, setMeetings] = React.useState([]);
  const [lastActivityText, setLastActivityText] = React.useState('—');
  const [activeProject, setActiveProject] = React.useState(null);
  const [activeProjectProgress, setActiveProjectProgress] = React.useState(0);
  const [application, setApplication] = React.useState(null);

  // --- Booking Wizard State ---
  const [showBookingWizard, setShowBookingWizard] = React.useState(false);
  const [bookingStep, setBookingStep] = React.useState(1); // 1: Reason, 2: Slot, 3: Confirm
  const [selectedReason, setSelectedReason] = React.useState(null);
  const [selectedDayOffset, setSelectedDayOffset] = React.useState(0);
  const [selectedSlot, setSelectedSlot] = React.useState(null);
  const [isSubmittingBooking, setIsSubmittingBooking] = React.useState(false);

  // --- Booking Wizard Logic ---
  const bookingSlots = React.useMemo(() => {
    const days = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      days.push({
        date: new Date(date),
        label: date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }),
        slots: ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00']
      });
      if (days.length >= 7) break;
    }
    return days;
  }, []);

  const handleBookingSubmit = async () => {
    if (!selectedReason || !selectedSlot) return;
    setIsSubmittingBooking(true);
    try {
      const [hour, minute] = selectedSlot.split(':');
      const start = new Date(bookingSlots[selectedDayOffset].date);
      start.setHours(parseInt(hour, 10), parseInt(minute, 10), 0, 0);

      const end = new Date(start);
      end.setMinutes(end.getMinutes() + selectedReason.duration);

      const data = {
        title: selectedReason.title,
        start: start.toISOString(),
        end: end.toISOString(),
        type: 'visio',
        status: 'scheduled',
        customer_id: customer?.id,
        notes: `Rendez-vous planifié via le dashboard. Motif : ${selectedReason.title}`
      };

      const record = await pb.collection('meetings').create(data);
      setMeetings(prev => [record, ...prev]);
      showToast("Rendez-vous confirmé ! 📅", "success");
      setShowBookingWizard(false);
      setBookingStep(1);
      setSelectedReason(null);
      setSelectedSlot(null);

      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (err) {
      showToast("Erreur lors de la réservation", "error");
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  // Message Attachments (Client)
  const [msgAttachments, setMsgAttachments] = React.useState([]);
  const msgFileInputRef = React.useRef(null);
  const dashboardContentRef = React.useRef(null);
  const [showWelcomeOverlay, setShowWelcomeOverlay] = React.useState(false);

  // Celebration Detection
  React.useEffect(() => {
    if (location.state?.celebration === 'login') {
      setShowWelcomeOverlay(true);
      // Clean up state immediately
      window.history.replaceState({}, document.title);

      // Explosion
      const duration = 5 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) return clearInterval(interval);

        const particleCount = 50 * (timeLeft / duration);
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
      }, 250);

      return () => clearInterval(interval);
    }
  }, [location.state]);

  // --- Premium UX: Smart Scroll on Tab Change ---
  React.useEffect(() => {
    if (dashboardContentRef.current) {
      // Offset to account for sticky nav or headers
      const offset = 100;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = dashboardContentRef.current.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, [location.pathname]);
  const sectionFromPath = (pathname) => {
    const p = String(pathname || '').toLowerCase();
    if (p.endsWith('/mon-compte') || p === '/mon-compte') return 'accueil';
    if (p.includes('/mon-compte/profil')) return 'profil';
    if (p.includes('/mon-compte/projets')) return 'projets';
    if (p.includes('/mon-compte/devis')) return 'devis';
    if (p.includes('/mon-compte/factures')) return 'factures';
    if (p.includes('/mon-compte/contrats')) return 'contrats';
    if (p.includes('/mon-compte/coffre-fort')) return 'coffre-fort';
    if (p.includes('/mon-compte/rendez-vous')) return 'rendez-vous';
    if (p.includes('/mon-compte/historique')) return 'historique';
    if (p.includes('/mon-compte/messages')) return 'messages';
    return 'accueil';
  };

  const handleInvoicePDF = async (invoice, mode = 'preview') => {
    try {
      const generator = new PDFGenerator();
      const cust = customer || (invoice.expand?.customer_id) || null;
      // fetch installments for this invoice
      let installments = [];
      try {
        const res = await pb.collection('invoice_installments').getList(1, 200, { sort: 'due_date', filter: `invoice_id = "${invoice.id}"` });
        installments = res?.items || [];
      } catch { }
      const s = settings ? { ...settings, _baseUrl: pb?.baseUrl } : { _baseUrl: pb?.baseUrl };
      await generator.generateInvoicePDF(invoice, cust, null, s, installments);
      if (mode === 'download') generator.download(`Facture_${invoice.invoice_number || invoice.id}.pdf`);
      else generator.preview();
    } catch (e) { try { alert("PDF indisponible pour l'instant"); } catch { } }
  };
  const [activeSection, setActiveSection] = React.useState(() => sectionFromPath(location.pathname));
  const [showLeonModal, setShowLeonModal] = React.useState(false); // New state for mobile chat modal
  const [quotes, setQuotes] = React.useState([]);
  const [invoices, setInvoices] = React.useState([]);
  const [contracts, setContracts] = React.useState([]);
  const [contractHtml, setContractHtml] = React.useState(null);
  const [fullScreenContract, setFullScreenContract] = React.useState(false);
  const [projects, setProjects] = React.useState([]);
  const [inboxMessages, setInboxMessages] = React.useState([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);
  const [loadingTab, setLoadingTab] = React.useState(false);
  const [settings, setSettings] = React.useState(null);
  const [profile, setProfile] = React.useState({ name: '', company: '', email: '', phone: '', address: '', postalCode: '', city: '' });
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [profileMsg, setProfileMsg] = React.useState('');
  const [selectedQuote, setSelectedQuote] = React.useState(null);
  const [consent, setConsent] = React.useState(false);
  const [sigName, setSigName] = React.useState('');
  const [sigLocation, setSigLocation] = React.useState(''); // Lieu de signature (Option 1)
  const [workingQuote, setWorkingQuote] = React.useState(false);
  const [quoteFilter, setQuoteFilter] = React.useState('all'); // all|sent|accepted|rejected|expired
  const [timeline, setTimeline] = React.useState([]);
  const [allDocuments, setAllDocuments] = React.useState([]);
  const [vaultFilter, setVaultFilter] = React.useState('all');
  const [selectedSpecId, setSelectedSpecId] = React.useState(null);

  // VIP Card 3D State
  const [cardRotation, setCardRotation] = React.useState({ x: 0, y: 0 });
  const [isCardDragging, setIsCardDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  // VIP Card Drag Logic
  React.useEffect(() => {
    const handleMove = (e) => {
      if (!isCardDragging) return;
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      setCardRotation(prev => ({
        // Invert X/Y for intuitive rotation
        x: Math.max(-60, Math.min(60, prev.x - deltaY * 0.5)),
        y: prev.y + deltaX * 0.5
      }));
      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleUp = () => setIsCardDragging(false);

    if (isCardDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isCardDragging, dragStart]);

  // Advisor logic (Dynamic or fallback to Léon)
  const advisor = React.useMemo(() => {
    const comm = customer?.expand?.commercial_id;
    if (comm) {
      return {
        name: comm.name || comm.username || 'Votre conseiller',
        avatar: comm.avatar ? pb.files.getUrl(comm, comm.avatar) : '/images/team/pi1.jpg',
        role: 'Votre conseiller dédié',
        bio: comm.bio || `Je suis ${comm.username || comm.name}, ravi de vous accompagner dans votre réussite digitale.`
      };
    }
    return {
      name: 'Léon',
      avatar: '/images/team/pi1.jpg',
      role: 'Votre conseiller dédié',
      bio: "De l'idée à la mise en ligne, je vous accompagne pas à pas pour faire de votre projet un succès."
    };
  }, [customer]);

  // Smart Actions Tracker
  const smartActions = React.useMemo(() => {
    const list = [];
    if (unreadMessages > 0) {
      list.push({
        id: 'msg',
        label: `Répondre à ${advisor.name || 'mon conseiller'}`,
        desc: `Vous avez ${unreadMessages} message(s) non lu(s).`,
        icon: '💬',
        path: '/mon-compte/messages',
        priority: 1
      });
    }
    if (unpaidInvoices > 0) {
      list.push({
        id: 'inv',
        label: 'Payer mes factures',
        desc: `${unpaidInvoices} facture(s) en attente de règlement.`,
        icon: '💳',
        path: '/mon-compte/factures',
        priority: 2
      });
    }
    if (pendingQuotes > 0) {
      list.push({
        id: 'quote',
        label: 'Signer mon devis',
        desc: `Un nouveau devis (#${pendingQuotes}) attend votre validation.`,
        icon: '✍️',
        path: '/mon-compte/devis',
        priority: 3
      });
    }
    if (pendingMeetings > 0) {
      list.push({
        id: 'meeting',
        label: 'Confirmer mon RDV',
        desc: `Vous avez un rendez-vous proposé à valider.`,
        icon: '📅',
        path: '/mon-compte/rendez-vous',
        priority: 4
      });
    }

    return list.sort((a, b) => a.priority - b.priority);
  }, [unreadMessages, unpaidInvoices, pendingQuotes, pendingMeetings, advisor]);

  // Sync activeSection with URL
  React.useEffect(() => {
    setActiveSection(sectionFromPath(location.pathname));
  }, [location, location.pathname]);

  // Pré-remplissage automatique de la signature
  React.useEffect(() => {
    if (selectedQuote && user?.name) {
      setSigName(`Lu et approuvé ${user.name}`);
    } else {
      setSigName('');
      setSigLocation(''); // Reset location too on close
    }
  }, [selectedQuote, user]);

  const [invoiceFilter, setInvoiceFilter] = React.useState('all'); // all|pending|partial|overdue|paid
  const [paymentChoice, setPaymentChoice] = React.useState('full'); // full|installments
  const [installmentsCount, setInstallmentsCount] = React.useState(3); // Total count (Deposit + N-1)
  const [showContractPreview, setShowContractPreview] = React.useState(false);

  // Helper to ensure plan is identical in Preview and Approval
  const calculatePaymentPlan = (quote, type, countInput) => {
    const pConf = quote.payment_config || {};
    const instDep = pConf.installments_deposit_percent ?? 20;

    let plan = {
      count: 1,
      frequency: 'monthly',
      start_date: new Date(),
      deposit_enabled: false,
      deposit_type: 'percent',
      deposit_value: 0
    };

    if (type === 'installments') {
      plan.count = Math.max(2, Number(countInput || 3));
      plan.deposit_enabled = true;
      plan.deposit_type = 'percent';
      plan.deposit_value = Number(instDep);
    } else if (type === 'half') {
      plan.count = 2;
      plan.deposit_enabled = true;
      plan.deposit_type = 'percent';
      plan.deposit_value = 50;
    }
    return plan;
  };

  React.useEffect(() => {
    (async () => {
      try {
        if (!user?.email) return;
        // Load customer by email
        const safeEmail = String(user.email).replace(/"/g, '\\"');
        const c = await pb.collection('customers').getList(1, 1, {
          filter: `(email = "${safeEmail}") || (email ~ "${safeEmail}")`,
          expand: 'commercial_id'
        }).then(r => r?.items?.[0] || null).catch(() => null);
        setCustomer(c);
        if (c) {
          setProfile({
            name: c.name || c.full_name || '',
            company: c.company || '',
            email: c.email || user.email || '',
            phone: c.phone || '',
            address: c.address || '',
            postalCode: c.postalCode || c.postal_code || '',
            city: c.city || ''
          });
        }
        if (!c) return;
        // Quotes pending (sent)
        try {
          const activeFilter = 'status = "sent"';
          let count = 0;
          try {
            // Try 'customer_id'
            const filter = `customer_id = "${c.id}" && ${activeFilter}`;
            const q = await pb.collection('quotes').getList(1, 1, { filter });
            count = q?.totalItems || 0;
          } catch {
            try {
              // Try 'client'
              const filter = `client = "${c.id}" && ${activeFilter}`;
              const q = await pb.collection('quotes').getList(1, 1, { filter });
              count = q?.totalItems || 0;
            } catch {
              try {
                // Try 'customer'
                const filter = `customer = "${c.id}" && ${activeFilter}`;
                const q = await pb.collection('quotes').getList(1, 1, { filter });
                count = q?.totalItems || 0;
              } catch { count = 0; }
            }
          }
          setPendingQuotes(count);
        } catch { setPendingQuotes(0); }
        // Invoices unpaid (pending/partial/overdue)
        try {
          // Try fetching with all unpaid statuses
          const activeFilter = '(payment_status = "pending" || payment_status = "partial" || payment_status = "overdue")';
          const filter = `customer_id = "${c.id}" && ${activeFilter}`;
          const inv = await pb.collection('invoices').getList(1, 1, { filter });
          setUnpaidInvoices(inv?.totalItems || 0);
        } catch {
          // Fallback to simple pending check
          try {
            const iRes = await pb.collection('invoices').getList(1, 1, { filter: `customer_id = "${c.id}" && payment_status = "pending"` });
            setUnpaidInvoices(iRes?.totalItems || 0);
          } catch { setUnpaidInvoices(0); }
        }

        // Contracts signed
        try {
          const cRes = await pb.collection('contracts').getList(1, 1, { filter: `customer = "${c.id}" && status = "signed"` });
          setSignedContractsCount(cRes?.totalItems || 0);
        } catch { setSignedContractsCount(0); }

        // Projects in progress
        try {
          const activeFilter = '(status != "completed" && status != "cancelled" && status != "archived")';
          let count = 0;
          try {
            const filter = `customer_id = "${c.id}" && ${activeFilter}`;
            const p = await pb.collection('projects').getList(1, 1, { filter });
            count = p?.totalItems || 0;
          } catch {
            try {
              const filter = `client = "${c.id}" && ${activeFilter}`;
              const p = await pb.collection('projects').getList(1, 1, { filter });
              count = p?.totalItems || 0;
            } catch {
              try {
                const filter = `customer = "${c.id}" && ${activeFilter}`;
                const p = await pb.collection('projects').getList(1, 1, { filter });
                count = p?.totalItems || 0;
              } catch { count = 0; }
            }
          }
          setPendingProjects(count);

          // Get the most recent active project for the Accueil progress bar
          try {
            const filter = `customer_id = "${c.id}" && status = "active"`;
            const pRes = await pb.collection('projects').getList(1, 1, { sort: '-created', filter });
            const p = pRes?.items?.[0];
            if (p) {
              setActiveProject(p);
              // Calculate progress from specs/roadmap
              try {
                const sRes = await pb.collection('specifications').getFirstListItem(`project="${p.id}"`);
                const roadmap = sRes?.roadmap || [];
                if (roadmap.length > 0) {
                  const done = roadmap.filter(t => t.done).length;
                  const total = roadmap.length;
                  setActiveProjectProgress(Math.round((done / total) * 100));
                } else {
                  setActiveProjectProgress(15); // Default start progress
                }
              } catch {
                setActiveProjectProgress(15);
              }
            }
          } catch { }

        } catch { setPendingProjects(0); }

        // Last activity: fallback to latest updated invoice
        try {
          const latest = await pb.collection('invoices').getList(1, 1, { sort: '-updated', filter: `customer_id = "${c.id}"` });
          const it = latest?.items?.[0];
          if (it) {
            const label = it.invoice_number || it.id;
            const d = new Date(it.updated || it.created);
            const dateStr = d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            setLastActivityText(`Facture ${label} mise à jour ${dateStr}`);
          }
        } catch { }

        // Fetch Meetings (Rendez-vous)
        // Fetch Meetings (Rendez-vous) & Count Pending
        try {
          const mFilter = `customer_id = "${c.id}"`;
          // Full list for state (limited to 50 for now)
          const m = await pb.collection('meetings').getList(1, 50, {
            filter: mFilter,
            sort: '-start',
            expand: 'with_user'
          });
          setMeetings(m?.items || []);

          // Count specifically "proposed" for the badge
          const pendingRes = await pb.collection('meetings').getList(1, 1, {
            filter: `customer_id = "${c.id}" && status = "proposed"`
          });
          setPendingMeetings(pendingRes?.totalItems || 0);

          // UNREAD MESSAGES
          try {
            // We check for messages sent TO the current user that are NOT read
            const msgRes = await pb.collection('messages').getList(1, 1, {
              filter: `to = "${user.id}" && read = false`
            });
            setUnreadMessages(msgRes?.totalItems || 0);
          } catch { }

          // --- Recent Activity Timeline ---
          try {
            // Get last 5 activity logs
            const logs = await pb.collection('activity_logs').getList(1, 5, {
              sort: '-created',
              // We filter by client email in context or search for related records
              // For simplicity, we try to find logs where context.user is current or generic messages
            });

            // Get last 3 unread messages
            const lastMsgs = await pb.collection('messages').getList(1, 3, {
              filter: `to = "${user.id}"`,
              sort: '-created'
            });

            const merged = [
              ...(logs.items.map(l => ({ ...l, type: 'log', time: l.created }))),
              ...(lastMsgs.items.map(m => ({ ...m, type: 'message', time: m.created, message: `Nouveau message de ${m.expand?.from?.name || 'votre conseiller'}` })))
            ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 5);

            setTimeline(merged);
          } catch { }

        } catch { }

        // --- Recruitment Application Tracking ---
        try {
          const app = await pb.collection('onboarding_states').getFirstListItem(`user = "${user.id}" && step ~ "candidature_"`, { sort: '-created' });
          setApplication(app);
        } catch {
          setApplication(null);
        }

      } catch (err) {
        console.error("Dashboard primary fetch error:", err);
      }
    })();
  }, [user?.email]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await pb.collection('settings').getList(1, 1, { sort: '-created' });
        const s = res?.items?.[0] || null;
        setSettings(s || null);
      } catch {
        setSettings(null);
      }
    })();
  }, []);

  // Sync on path change
  React.useEffect(() => {
    setActiveSection(sectionFromPath(location.pathname));
  }, [location.pathname]);

  // Load tab data when needed (based on section)
  React.useEffect(() => {
    (async () => {
      if (!customer) return;
      const sec = (activeSection || '').toLowerCase();
      setLoadingTab(true);
      try {
        if (sec === 'devis') {
          const qRes = await pb.collection('quotes').getList(1, 50, { sort: '-created', filter: `customer_id = "${customer.id}"` });
          setQuotes(qRes?.items || []);
        } else if (sec === 'factures') {
          const iRes = await pb.collection('invoices').getList(1, 50, { sort: '-created', filter: `customer_id = "${customer.id}"` });
          setInvoices(iRes?.items || []);
        } else if (sec === 'contrats') {
          const cRes = await pb.collection('contracts').getList(1, 50, { sort: '-created', filter: `customer = "${customer.id}"` });
          setContracts(cRes?.items || []);
        } else if (sec === 'rendez-vous') {
          const mFilter = `customer_id = "${customer.id}"`;
          const mRes = await pb.collection('meetings').getList(1, 50, { filter: mFilter, sort: '-start', expand: 'with_user' });
          setMeetings(mRes?.items || []);
        } else if (sec === 'messages') {
          try {
            const filter = `to = "${user.id}" || from = "${user.id}"`;
            const mRes = await pb.collection('messages').getList(1, 50, { filter, sort: 'created', expand: 'from,to' });
            setInboxMessages(mRes?.items || []);
          } catch { }
        } else if (sec === 'projets') {
          // Projects in progress
          try {
            const activeFilter = '(status != "completed" && status != "cancelled" && status != "archived")';
            let count = 0;
            try {
              const filter = `customer_id = "${customer.id}" && ${activeFilter}`;
              const p = await pb.collection('projects').getList(1, 1, { filter });
              count = p?.totalItems || 0;
            } catch {
              try {
                const filter = `client = "${customer.id}" && ${activeFilter}`;
                const p = await pb.collection('projects').getList(1, 1, { filter });
                count = p?.totalItems || 0;
              } catch {
                try {
                  const filter = `customer = "${customer.id}" && ${activeFilter}`;
                  const p = await pb.collection('projects').getList(1, 1, { filter });
                  count = p?.totalItems || 0;
                } catch { count = 0; }
              }
            }
            setPendingProjects(count);
          } catch { setPendingProjects(0); }
          setProjectsLoading(true);
          try {
            // Priority to customer_id
            const filter = `customer_id = "${customer.id}" && (status != "completed" && status != "cancelled" && status != "archived")`;
            const pRes = await pb.collection('projects').getList(1, 200, { sort: '-created', filter });
            setProjects(pRes?.items || []);
          } catch (e1) {
            try {
              // Priority to client
              const pRes = await pb.collection('projects').getList(1, 200, { sort: '-created', filter: `client = "${customer.id}"` });
              setProjects(pRes?.items || []);
            } catch (e2) {
              try {
                // Priority to customer
                const pRes = await pb.collection('projects').getList(1, 200, { sort: '-created', filter: `customer = "${customer.id}"` });
                setProjects(pRes?.items || []);
              } catch {
                setProjects([]);
              }
            }
          } finally {
            setProjectsLoading(false);
          }
        } else if (sec === 'coffre-fort') {
          // Aggregate everything
          const docs = [];
          try {
            const iRes = await pb.collection('invoices').getList(1, 50, { sort: '-created', filter: `customer_id = "${customer.id}"` });
            docs.push(...iRes.items.map(it => ({ ...it, vaultType: 'invoice', label: it.invoice_number || 'Facture', date: it.created })));
          } catch { }
          try {
            const cRes = await pb.collection('contracts').getList(1, 50, { sort: '-created', filter: `customer = "${customer.id}"` });
            docs.push(...cRes.items.map(it => ({ ...it, vaultType: 'contract', label: `Contrat #${it.id.substring(0, 6)}`, date: it.created })));
          } catch { }
          try {
            const qRes = await pb.collection('quotes').getList(1, 50, { sort: '-created', filter: `customer_id = "${customer.id}"` });
            docs.push(...qRes.items.map(it => ({ ...it, vaultType: 'quote', label: it.quote_number || 'Devis', date: it.created })));
          } catch { }
          try {
            const pRes = await pb.collection('projects').getFullList({ filter: `customer = "${customer.id}"` });
            const pIds = pRes.map(p => p.id);
            if (pIds.length > 0) {
              const sRes = await pb.collection('specifications').getFullList({
                filter: pIds.map(id => `project = "${id}"`).join(' || '),
                expand: 'project'
              });
              docs.push(...sRes.map(it => ({
                ...it,
                vaultType: 'specification',
                label: `Cahier des charges - ${it.expand?.project?.name || 'Projet'}`,
                date: it.created
              })));
            }
          } catch { }

          setAllDocuments(docs.sort((a, b) => new Date(b.date) - new Date(a.date)));
        }
      } catch {
        if (sec === 'devis') setQuotes([]);
        if (sec === 'factures') setInvoices([]);
        if (sec === 'projets') setProjects([]);
        if (sec === 'rendez-vous') setMeetings([]);
        if (sec === 'contrats') setContracts([]);
      } finally {
        setLoadingTab(false);
      }
    })();
  }, [activeSection, customer]);

  // Real-time subscription
  React.useEffect(() => {
    let unsubMeetings = null;
    let unsubMessages = null;
    let unsubQuotes = null;
    let unsubInvoices = null;
    let unsubProjects = null;

    if (customer?.id && user?.id) {
      // 1. Meetings
      pb.collection('meetings').subscribe('*', async function (e) {
        const mFilter = `customer_id = "${customer.id}"`;
        // Refetch list if on meeting tab
        if (activeSection === 'rendez-vous') {
          pb.collection('meetings').getList(1, 50, { filter: mFilter, sort: '-start', expand: 'with_user' })
            .then(res => setMeetings(res?.items || [])).catch(() => { });
        }

        // Update badge
        try {
          const pRes = await pb.collection('meetings').getList(1, 1, { filter: `customer_id = "${customer.id}" && status = "proposed"` });
          setPendingMeetings(pRes?.totalItems || 0);
        } catch { }
      }).then(u => unsubMeetings = u).catch(console.error);

      // 2. Messages
      pb.collection('messages').subscribe('*', async function (e) {
        try {
          const msgRes = await pb.collection('messages').getList(1, 1, { filter: `to = "${user.id}" && read = false` });
          setUnreadMessages(msgRes?.totalItems || 0);
        } catch { }
        if (window.location.pathname.includes('/messages') || activeSection === 'messages') {
          try {
            const filter = `to = "${user.id}" || from = "${user.id}"`;
            const mRes = await pb.collection('messages').getList(1, 50, { filter, sort: 'created', expand: 'from,to' });
            setInboxMessages(mRes?.items || []);
          } catch { }
        }
      }).then(u => unsubMessages = u).catch(console.error);

      // 3. Quotes (Devis)
      pb.collection('quotes').subscribe('*', async function (e) {
        // Refetch badge (Pending)
        try {
          const activeFilter = 'status = "sent"';
          const filter = `(customer_id = "${customer.id}" || client = "${customer.id}" || customer = "${customer.id}") && ${activeFilter}`;
          const q = await pb.collection('quotes').getList(1, 1, { filter });
          setPendingQuotes(q?.totalItems || 0);
        } catch { }

        // Refetch List if on Tab
        if (activeSection === 'devis') {
          refreshQuotes();
        }
        // Also refresh projects if a quote turned into a project? (Usually separate)
      }).then(u => unsubQuotes = u).catch(console.error);

      // 4. Invoices (Factures)
      pb.collection('invoices').subscribe('*', async function (e) {
        // Refetch badge (Unpaid)
        try {
          const activeFilter = '(payment_status = "pending" || payment_status = "partial" || payment_status = "overdue")';
          const filter = `customer_id = "${customer.id}" && ${activeFilter}`;
          const inv = await pb.collection('invoices').getList(1, 1, { filter });
          setUnpaidInvoices(inv?.totalItems || 0);
        } catch { }

        // Refetch list
        if (activeSection === 'factures') {
          const iRes = await pb.collection('invoices').getList(1, 50, { sort: '-created', filter: `customer_id = "${customer.id}"` });
          setInvoices(iRes?.items || []);
        }
      }).then(u => unsubInvoices = u).catch(console.error);

      // 5. Projects
      pb.collection('projects').subscribe('*', async function (e) {
        // Refetch badge (Active)
        try {
          const activeFilter = '(status != "completed" && status != "cancelled" && status != "archived")';
          const filter = `(customer_id = "${customer.id}" || client = "${customer.id}" || customer = "${customer.id}") && ${activeFilter}`;
          const p = await pb.collection('projects').getList(1, 1, { filter });
          setPendingProjects(p?.totalItems || 0);
        } catch { }

        // Refetch list
        if (activeSection === 'projets') {
          // Re-trigger the load tab logic or manual fetch (simplified here)
          const filter = `(customer_id = "${customer.id}" || client = "${customer.id}" || customer = "${customer.id}") && (status != "completed" && status != "cancelled" && status != "archived")`;
          const pRes = await pb.collection('projects').getList(1, 200, { sort: '-created', filter });
          setProjects(pRes?.items || []);
        }
      }).then(u => unsubProjects = u).catch(console.error);
    }

    return () => {
      if (pb.realtime.isConnected) {
        if (unsubMeetings) unsubMeetings();
        if (unsubMessages) unsubMessages();
        if (unsubQuotes) unsubQuotes();
        if (unsubInvoices) unsubInvoices();
        if (unsubProjects) unsubProjects();
      }
    };
  }, [customer, user, activeSection]);

  const statusLabel = (s) => ({ draft: 'Brouillon', sent: 'Envoyé', accepted: 'Accepté', rejected: 'Refusé', expired: 'Expiré' }[s] || s || 'Brouillon');
  const invoicePaymentLabel = (s) => ({ pending: 'En attente', partial: 'Partiel', overdue: 'En retard', paid: 'Payée' }[s] || s || 'En attente');
  const isExpired = (q) => {
    try {
      if (q?.status === 'accepted') return false;
      const d = q?.valid_until ? new Date(q.valid_until) : null;
      if (!d) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      return d.getTime() < today.getTime();
    } catch { return false; }
  };

  const handleQuotePDF = async (quote, mode = 'preview') => {
    try {
      let cust = customer || (quote.expand?.customer_id) || null;
      let vehicle = null;
      try { if (!cust && quote.customer_id) cust = await pb.collection('customers').getOne(quote.customer_id); } catch { }
      try {
        const vehId = quote.vehicle_id || quote.project_id || null;
        if (vehId) vehicle = await pb.collection('projects').getOne(vehId);
      } catch { }
      const generator = new PDFGenerator();
      const s = settings ? { ...settings, _baseUrl: pb?.baseUrl } : { _baseUrl: pb?.baseUrl };
      await generator.generateQuotePDF(quote, cust, vehicle, s);
      if (mode === 'download') generator.download(`Devis_${quote.quote_number || quote.id}.pdf`);
      else generator.preview();
    } catch (e) { try { alert("PDF indisponible pour l'instant"); } catch { } }
  };

  const handleContractPDF = async (quote) => {
    try {
      const contract = contracts.find(c => c.quote === quote.id);
      if (!contract) {
        alert("Contrat introuvable. Veuillez recharger la page.");
        return;
      }

      let settings = null;
      try { settings = await pb.collection('settings').getFirstListItem(''); } catch { }

      const generator = new PDFGenerator();
      const s = settings ? { ...settings, _baseUrl: pb?.baseUrl } : { _baseUrl: pb?.baseUrl };
      await generator.generateContractPDF(contract, s);
      generator.download(`Contrat_${quote.quote_number || quote.id}.pdf`);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la génération du PDF du contrat.");
    }
  };

  /* Gestion Preview Contrat depuis l'onglet Contrats */
  const previewContractPDF = async (contract) => {
    try {
      let settings = null;
      try { settings = await pb.collection('settings').getFirstListItem(''); } catch { }
      const generator = new PDFGenerator();
      const s = settings ? { ...settings, _baseUrl: pb?.baseUrl } : { _baseUrl: pb?.baseUrl };
      await generator.generateContractPDF(contract, s);
      generator.preview();
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la génération du PDF.");
    }
  };

  const downloadContractPDF = async (contract) => {
    try {
      let settings = null;
      try { settings = await pb.collection('settings').getFirstListItem(''); } catch { }
      const generator = new PDFGenerator();
      const s = settings ? { ...settings, _baseUrl: pb?.baseUrl } : { _baseUrl: pb?.baseUrl };
      await generator.generateContractPDF(contract, s);
      const filename = `Contrat_${contract.expand?.quote?.quote_number || contract.id}.pdf`;
      generator.download(filename);
    } catch (e) {
      console.error(e);
      alert("Erreur lors du téléchargement du PDF.");
    }
  };

  const refreshQuotes = async () => {
    try {
      if (!customer) return;
      const qRes = await pb.collection('quotes').getList(1, 50, { sort: '-created', filter: `customer_id = "${customer.id}"` });
      setQuotes(qRes?.items || []);

      const cRes = await pb.collection('contracts').getList(1, 100, {
        filter: `customer = "${customer.id}"`,
        expand: 'quote,customer,project',
        sort: '-created'
      });
      setContracts(cRes?.items || []);
    } catch { }
  };
  /* 
   * GÉNÉRATEUR DE CONTRAT INTELLIGENT (APPROCHE "LEGO")
   * Analyse le contenu du devis pour assembler les clauses juridiques adéquates.
   */
  const generateContractHtml = (quote, cust, plan, totalAmt, location = '', sigText = '') => {
    const totalFormatted = totalAmt.toFixed(2) + ' € TTC';
    const projectName = (quote.vehicle_id && typeof quote.vehicle_id === 'object' ? quote.vehicle_id.name : 'Votre Projet') || quote.project_name || 'Prestation Numérique';
    const date = new Date().toLocaleDateString('fr-FR');
    const madeIn = location.trim() || '____________________'; // Ligne vide si pas de ville

    // 1. ANALYSE DU DEVIS : Quels modules activer ?
    const itemsText = (quote.items || []).map(i => (i.description || '') + ' ' + (i.title || '')).join(' ').toLowerCase();

    // Mots-clés déclencheurs
    const isWeb = /site|web|développement|app|intégration|wordpress|react/i.test(itemsText) || itemsText.length === 0; // Par défaut Web si vide
    const isCM = /community|réseaux|social|instagram|facebook|linkedin|post|publication|éditorial/i.test(itemsText);
    const isSEO = /seo|référencement|audit|backlink|optimisation/i.test(itemsText);
    const isMaint = /maintenance|hébergement|support|mise à jour|sauvegarde/i.test(itemsText);

    // 2. CONSTRUCTION DU TEXTE DE PAIEMENT
    let paymentText = '';
    if (plan.count === 1) {
      paymentText = `<strong>Modalité : Paiement Comptant</strong><br>Le montant total de ${totalFormatted} est dû à la signature du présent contrat.`;
    } else if (plan.count === 2 && plan.deposit_value === 50) {
      const half = (totalAmt / 2).toFixed(2);
      paymentText = `<strong>Modalité : Paiement 50/50</strong><br>• Acompte de 50% (${half} €) à la signature.<br>• Solde de 50% (${half} €) à la livraison ou fin de prestation.`;
    } else {
      const depositPct = plan.deposit_value;
      const depositAmount = (totalAmt * (depositPct / 100)).toFixed(2);
      const remaining = totalAmt - depositAmount;
      const monthCount = Math.max(1, plan.count - 1);
      const monthlyAmount = (remaining / monthCount).toFixed(2);
      paymentText = `<strong>Modalité : Paiement en ${plan.count} fois</strong><br>• Acompte de ${depositPct}% (${depositAmount} €) à la signature.<br>• Le solde de ${remaining.toFixed(2)} € en ${monthCount} mensualités de ${monthlyAmount} €.<br>• La première mensualité est due un mois après la signature du présent contrat.`;
    }

    // 3. ASSEMBLAGE DES CLAUSES
    let clausesHtml = '';

    // --- MODULE WEB ---
    if (isWeb) {
      clausesHtml += `
        <h3 style="margin-top:20px; border-bottom:1px solid #eee; padding-bottom:5px;">Spécificités Création Web / App</h3>
        <p><strong>Cahier des charges :</strong> Le devis validé tient lieu de cahier des charges fonctionnel minimal. Toutes fonctionnalités non listées feront l'objet d'un avenant.</p>
        <p><strong>Référencement (SEO) :</strong> La prestation inclut une optimisation "SEO de base" (structure technique, balises meta). Pour une stratégie de positionnement avancée, un devis spécifique "SEO" est nécessaire.</p>
        <p><strong>Recette et Livraison :</strong> À compter de la livraison, le Client dispose de 10 jours ouvrés pour valider le projet (recette). Passé ce délai, le projet est réputé accepté sans réserve.</p>
        <p><strong>Garantie :</strong> Le Prestataire assure une garantie de correction de bugs techniques pendant <strong>6 mois</strong> après la livraison, hors intervention tiers ou mauvaise manipulation client.</p>
      `;
    }

    // --- MODULE COMMUNITY MANAGEMENT ---
    if (isCM) {
      clausesHtml += `
        <h3 style="margin-top:20px; border-bottom:1px solid #eee; padding-bottom:5px;">Spécificités Community Management</h3>
        <p><strong>Forfait & Mandat :</strong> Le Client autorise le Prestataire à publier en son nom sur <strong>2 réseaux sociaux</strong> définis, à raison de <strong>2 publications par semaine</strong>.</p>
        <p><strong>Prestations incluses :</strong> Le service comprend la planification éditoriale, la création des visuels et textes, l'animation de la communauté ainsi que la modération standard des commentaires publics.</p>
        <p><strong>Validation & Limites :</strong> Le Client doit valider les contenus sous 48h (décalage possible si retard). Les vidéos complexes, campagnes publicitaires (Ads) et la gestion du SAV en messagerie privée ne sont pas incluses, sauf mention contraire.</p>
      `;
    }

    // --- MODULE SEO ---
    if (isSEO) {
      clausesHtml += `
        <h3 style="margin-top:20px; border-bottom:1px solid #eee; padding-bottom:5px;">Spécificités Référencement (SEO)</h3>
        <p><strong>Obligation de moyens :</strong> Le SEO dépendant des algorithmes tiers (Google), le Prestataire est tenu à une obligation de moyens et non de résultats (positionnement garanti impossible).</p>
        <p><strong>Durée :</strong> Les résultats du référencement naturel peuvent prendre plusieurs mois avant d'être visibles.</p>
      `;
    }

    // --- MODULE MAINTENANCE ---
    if (isMaint) {
      clausesHtml += `
        <h3 style="margin-top:20px; border-bottom:1px solid #eee; padding-bottom:5px;">Spécificités Maintenance & Hébergement</h3>
        <p><strong>Intervention :</strong> Le Prestataire s'engage à intervenir sous 48h ouvrées en cas de panne critique du site.</p>
        <p><strong>Renouvellement :</strong> Les services d'hébergement/maintenance sont tacitement reconduits annuellement, sauf dénonciation par AR 1 mois avant l'échéance.</p>
      `;
    }

    // 4. RENDU FINAL (TEMPLATE GLOBAL)
    return `
              <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px;">
                <h2 style="text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 30px;">CONTRAT DE PRESTATION DE SERVICES</h2>

                <div style="display: flex; justify-content: space-between; margin-bottom: 40px; background: #f8f9fa; padding: 15px; border-radius: 8px;">
                  <div style="width: 45%;">
                    <h4 style="margin: 0 0 5px 0; color: #666; text-transform: uppercase; font-size: 0.8rem;">Le Prestataire</h4>
                    <strong>INFOCOLAB</strong><br>
                      Siret 838 220 325 00016
                  </div>
                  <div style="width: 45%; text-align: right;">
                    <h4 style="margin: 0 0 5px 0; color: #666; text-transform: uppercase; font-size: 0.8rem;">Le Client</h4>
                    <strong>${cust.name || 'Client'}</strong> ${cust.company ? `(${cust.company})` : ''}<br>
                      ${cust.address || ''}<br>
                        ${cust.zip || ''} ${cust.city || ''}
                      </div>
                  </div>


                  <h3>1. Objet du Contrat</h3>
                  <p>Le présent contrat a pour objet la réalisation de : <strong>"${projectName}"</strong> (selon Devis N°${quote.quote_number || quote.id}). Le Prestataire s’engage à fournir les services définis.</p>

                  <h3>2. Collaboration & Délais</h3>
                  <p><strong>Engagements du Client :</strong> Le Client s’engage à fournir les éléments nécessaires (textes, images, accès) dans les meilleurs délais pour ne pas bloquer la production.</p>
                  <p><strong>Délais :</strong> Le projet sera réalisé dans les délais convenus après réception complète des éléments.</p>

                  <h3>3. Dispositions Spécifiques aux Services</h3>
                  ${clausesHtml}

                  <h4 style="margin-top:20px; border-bottom:1px solid #eee; padding-bottom:5px; font-size: 1.1em;">Hébergement & Services Tiers</h4>
                  <p>Sauf si inclus explicitement dans une offre de maintenance mensuelle, <strong>l'hébergement et le nom de domaine</strong> (ainsi que tout service tiers type abonnements plugins, polices payantes) sont à la charge du Client et doivent être renouvelés annuellement par ses soins pour garantir la continuité du service.</p>

                  <h3>4. Prix et Modalités de Paiement</h3>
                  <p>Le montant total est de <strong>${totalFormatted}</strong>.</p>
                  <div style="background: #ecfdf5; padding: 15px; border-radius: 6px; border: 1px solid #10b981; margin: 15px 0; font-size: 0.95rem;">
                    ${paymentText}
                  </div>
                  <p style="font-size: 0.9rem; color: #666;">Le démarrage de la prestation est conditionné à la réception du premier versement (acompte ou total).</p>

                  <h3>5. Propriété Intellectuelle (Cession)</h3>
                  <p>Sauf mention contraire (notamment pour les licences logicielles tierces), le Prestataire cède au Client l'ensemble des droits d'exploitation sur les livrables réalisés sur mesure, à compter du <strong>paiement intégral</strong> de la facture finale.</p>

                  <h3>6. Litiges</h3>
                  <p>En cas de litige, les parties privilégieront une résolution amiable. À défaut, les tribunaux du siège du Prestataire seront seuls compétents.</p>

                  <div style="margin-top: 50px; border-top: 2px dashed #ccc; padding-top: 20px;">
                    <p>Fait à <strong>${madeIn}</strong>, le ${date}</p>
                    <div style="display: flex; justify-content: space-between; margin-top: 30px;">
                      <div style="width: 45%;">
                        <strong>Pour le Prestataire</strong><br>
                          <div style="margin-top: 10px; color: #666; font-style: italic; background: #eee; padding: 10px; text-align: center; border-radius: 4px;">
                            Signature électronique<br>Validée par système
                          </div>
                      </div>
                      <div style="width: 45%;">
                        <strong>Pour le Client</strong><br>
                          <div style="margin-top: 10px; border: 1px solid #333; padding: 15px; border-radius: 4px; min-height: 80px;">
                            <span style="font-size: 1.1rem; font-family: 'Courier New', monospace; font-weight: bold; white-space: pre-wrap;">${sigText || '"Lu et approuvé..."'}</span><br>
                              <span style="display: block; margin-top: 8px;">${cust.name}</span>
                              <em style="display: block; margin-top: 5px; font-size: 0.8rem; color: #666;">(Signé numériquement le ${date})</em>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
                `;
  };

  const approveSelectedQuote = async () => {
    if (!selectedQuote || !customer) return;
    if (!consent) { alert('Veuillez accepter les CGV'); return; }
    setWorkingQuote(true);
    try {
      const now = new Date().toISOString();
      await pb.collection('quotes').update(selectedQuote.id, {
        status: 'accepted',
        accepted_at: now,
        accepted_by: user?.email || '',
        signature_name: sigName || (user?.name || ''),
        signature_consent: true,
        signature_at: now,
      });
      try { await pb.collection('activity_logs').create({ collection: 'quotes', record_id: selectedQuote.id, action: 'approve', message: 'Devis accepté par client', context: { user: user?.email } }); } catch { }

      // Create invoice from quote (Fixed Logic)
      let createdInv = null;
      try {
        // Check regular invoice existence
        const existing = await pb.collection('invoices').getList(1, 1, { filter: `quote_id="${selectedQuote.id}"` });

        if (existing.totalItems === 0) {
          // Fetch fresh settings for numbering
          let invPrefix = 'FACT';
          let invCounter = 1;
          let paymentTerms = 30;
          let settingsId = null;
          try {
            const sRes = await pb.collection('settings').getList(1, 1, { sort: '-created' });
            if (sRes.items.length > 0) {
              const s = sRes.items[0];
              settingsId = s.id;
              invPrefix = s.invoice_prefix || 'FACT';
              invCounter = Number(s.invoice_counter || 1);
              paymentTerms = Number(s.invoice_payment_terms) || 30;
            }
          } catch { }

          const nextInvNumber = `${invPrefix}-${String(invCounter).padStart(4, '0')}`;
          const due = new Date(Date.now() + paymentTerms * 86400000);
          const calcDueDate = (!isNaN(due.getTime())) ? due.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

          const payload = {
            quote_id: selectedQuote.id,
            customer_id: selectedQuote.customer_id || (customer?.id),
            vehicle_id: selectedQuote.vehicle_id || null,
            project_id: selectedQuote.project_id || selectedQuote.vehicle_id || null,
            invoice_number: nextInvNumber,
            status: 'draft',
            payment_status: 'pending',
            issued_date: now.slice(0, 10),
            due_date: calcDueDate,
            items: Array.isArray(selectedQuote.items) ? selectedQuote.items : [],
            discount_type: selectedQuote.discount_type || '',
            discount_value: Number(selectedQuote.discount_value || 0),
            discount_label: selectedQuote.discount_label || '',
            tax_enabled: typeof selectedQuote.tax_enabled === 'boolean' ? selectedQuote.tax_enabled : true,
            subtotal: Number(selectedQuote.subtotal || 0),
            discount_total: Number(selectedQuote.discount_total || 0),
            tax_amount: Number(selectedQuote.tax_amount || 0),
            total: Number(selectedQuote.total || 0),
            tax_rate: Number(selectedQuote.tax_rate || 20),
          };

          createdInv = await pb.collection('invoices').create(payload);

          // Update counter
          if (settingsId) {
            await pb.collection('settings').update(settingsId, { invoice_counter: invCounter + 1 });
          }
        }
      } catch (invErr) {
        console.error("Invoice creation failed", invErr);
        alert("Attention: Devis validé mais erreur création facture: " + (invErr.message || JSON.stringify(invErr)));
      }

      // Installments logic (only if invoice created)
      if (createdInv) {
        try {
          const invId = createdInv.id;
          const total = Number(selectedQuote.total || 0);
          // Determine Plan based on User Choice (Override Quote defaults)
          const pConf = selectedQuote.payment_config || {};
          const instDep = pConf.installments_deposit_percent ?? 20;

          let plan = {
            count: 1,
            frequency: 'monthly',
            start_date: new Date(),
            deposit_enabled: false,
            deposit_type: 'percent',
            deposit_value: 0
          };

          if (paymentChoice === 'installments') {
            plan.count = Math.max(2, Number(installmentsCount || 3));
            plan.deposit_enabled = true;
            plan.deposit_type = 'percent';
            plan.deposit_value = Number(instDep);
          } else if (paymentChoice === 'half') {
            plan.count = 2;
            plan.deposit_enabled = true;
            plan.deposit_type = 'percent';
            plan.deposit_value = 50;
          }

          if (total > 0) {
            const n = Math.max(1, plan.count);
            const parts = new Array(n).fill(0);
            let deposit = 0;
            if (plan.deposit_enabled) {
              if (plan.deposit_type === 'percent') deposit = Math.max(0, Math.min(total, Number(((total * plan.deposit_value) / 100).toFixed(2))));
              else deposit = Math.max(0, Math.min(total, Number((plan.deposit_value).toFixed(2))));
            }
            // Logic for installments split
            if (deposit > 0) {
              parts[0] = deposit;
              const rest = Math.max(0, total - deposit);
              const m = Math.max(1, n - 1);
              if (m > 0) {
                const base = Math.floor((rest / m) * 100) / 100;
                for (let i = 1; i < n; i++) parts[i] = base;
                const sumRest = parts.slice(1).reduce((a, b) => a + b, 0);
                parts[n - 1] = Number((rest - sumRest + parts[n - 1]).toFixed(2));
              }
            } else {
              const base = Math.floor((total / n) * 100) / 100;
              for (let i = 0; i < n; i++) parts[i] = base;
              const sum = parts.reduce((a, b) => a + b, 0);
              parts[n - 1] = Number((total - sum + parts[n - 1]).toFixed(2));
            }

            for (let i = 0; i < n; i++) {
              const due = new Date(plan.start_date || new Date());
              if (plan.frequency === 'monthly') due.setMonth(due.getMonth() + i);
              else if (plan.frequency === 'biweekly') due.setDate(due.getDate() + i * 14);
              else if (plan.frequency === 'weekly') due.setDate(due.getDate() + i * 7);

              const payloadIt = {
                invoice_id: invId,
                customer_id: createdInv.customer_id,
                project_id: createdInv.project_id,
                label: n === 1 ? 'Paiement' : (i === 0 && deposit > 0 ? (paymentChoice === 'half' ? 'Acompte (50%)' : `Acompte (${instDep}%)`) : (paymentChoice === 'half' ? 'Solde à livraison' : `Échéance ${i + 1}/${n}`)),
                due_date: (!isNaN(due.getTime())) ? due.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                amount_due: Number(parts[i].toFixed(2)),
                amount_paid: 0,
                status: 'pending',
                ...(deposit > 0 && i === 0 ? { is_deposit: true } : {})
              };
              await pb.collection('invoice_installments').create(payloadIt);
            }

            // meta update on invoice
            const meta = {};
            if (deposit > 0) {
              meta.deposit_expected = Number(deposit.toFixed(2));
              meta.deposit_config = { enabled: true, type: plan.deposit_type, value: plan.deposit_value };
            }
            meta.installments_generated = true;
            meta.installments_count = n;
            await pb.collection('invoices').update(invId, meta);
          }
          // Generate Signed Contract
          try {
            const contractHtml = generateContractHtml(selectedQuote, customer, plan, total, sigLocation, sigName);
            await pb.collection('contracts').create({
              title: `Contrat - ${(selectedQuote.expand?.vehicle_id?.name || selectedQuote.project_name || 'Projet')}`,
              content: contractHtml,
              customer: customer.id,
              project: (typeof selectedQuote.vehicle_id === 'object' ? selectedQuote.vehicle_id.id : selectedQuote.vehicle_id) || '',
              quote: selectedQuote.id,
              status: 'signed',
              signed_at: now,
              signature: sigName || user?.name || 'Client',
              terms_accepted: true,
            });
          } catch (cErr) { console.error("Contract creation error", cErr); }
        } catch (instErr) { console.error("Installments error", instErr); }
      }

      showToast('Devis accepté. La ou les facture(s) ont été créée(s).', 'success');
      setSelectedQuote(null); setConsent(false);
      await refreshQuotes();
      navigate('/mon-compte/factures');
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de l\'acceptation: ' + (e.message || e), 'error');
    } finally { setWorkingQuote(false); }
  };

  const rejectQuote = async (q) => {
    if (!q || !customer) return;
    if (!window.confirm('Refuser ce devis ?')) return;
    setWorkingQuote(true);
    try {
      const now = new Date().toISOString();
      await pb.collection('quotes').update(q.id, { status: 'rejected', rejected_at: now, rejected_by: user?.email || '' });
      try { await pb.collection('activity_logs').create({ collection: 'quotes', record_id: q.id, action: 'reject', message: 'Devis refusé par client', context: { user: user?.email } }); } catch { }
      await refreshQuotes();
    } catch {
      showToast('Refus impossible', 'error');
    } finally { setWorkingQuote(false); }
  };

  return (
    <div className="dashboard-page input-mode">
      {/* Mobile Floating Dock (Fixed) - Hidden on Desktop via CSS in component */}
      <div className="mobile-dock-only">
        <FloatingSidebar counts={{ projects: pendingProjects, quotes: pendingQuotes, invoices: unpaidInvoices, meetings: pendingMeetings, messages: unreadMessages, contracts: signedContractsCount }} />
      </div>

      <div className="dashboard-content-wrapper" style={{ minHeight: '60vh', padding: '20px 20px 40px' }}>
        <div className="dashboard-grid">

          {/* 1. Left Sidebar Navigation (Desktop Sticky) */}
          <div className="sidebar-col desktop-sidebar-only">
            <div className="sidebar-sticky-wrapper">
              <FloatingSidebar embedded={true} counts={{ projects: pendingProjects, quotes: pendingQuotes, invoices: unpaidInvoices, meetings: pendingMeetings, messages: unreadMessages, contracts: signedContractsCount }} />
            </div>
          </div>

          {/* 2. Main Content Center */}
          <div style={{ minWidth: 0 }}>
            <h1 ref={dashboardContentRef}>Tableau de bord</h1>
            <p>Bonjour {user?.name || user?.email} 👋</p>

            {/* Local styles for Mon Compte */}
            <style>{`
        /* Grid Layout: [Nav 240px] [Content 1fr] [Advisor 300px] */
        .dashboard-page { 
            color: #1e293b;
        }
        .dark .dashboard-page {
            color: #f1f5f9;
        }
        .dashboard-page h1, .dashboard-page h2, .dashboard-page h3 {
            color: #0f172a;
        }
        .dark .dashboard-page h1, .dark .dashboard-page h2, .dark .dashboard-page h3 {
            color: #f8fafc;
        }

        /* Dashboard Footer Styles */
        .hover-scale:hover { transform: scale(1.05); }
        .dashboard-footer a:hover { color: var(--primary, #6366f1) !important; }
        .dark .dashboard-footer { 
          background: #0f172a !important; 
          border-top-color: rgba(99, 102, 241, 0.2) !important;
          box-shadow: 0 -10px 40px rgba(0,0,0,0.3) !important;
        }
        .dark .footer-col h5 { color: #f8fafc !important; }
        .dark .footer-col a { color: #94a3b8 !important; }
        .dark .footer-col a:hover { color: #818cf8 !important; }
        .dark .security-badge { 
          background: #1e293b !important; 
          border-color: #334155 !important; 
          color: #94a3b8 !important;
        }
        .dark span[style*="var(--text-primary"] {
          color: #f1f5f9 !important;
        }
        @media (max-width: 768px) {
          .install-banner { text-align: center; justify-content: center !important; }
          .install-btn { width: 100%; }
        }


        .dashboard-grid { 
            display: grid; 
            grid-template-columns: 240px 1fr 300px; 
            gap: 32px; 
            max-width: 1380px; 
            margin: 0 auto; 
            align-items: stretch; /* Permet aux colonnes de faire toute la hauteur */
        }
        
        .sidebar-sticky-wrapper, .advisor-sticky-wrapper {
            position: sticky; 
            top: calc(var(--nav-height, 70px) + 20px); 
            z-index: 100;
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }

        .tab-content-anim {
            animation: tabFadeIn 0.4s cubic-bezier(0.23, 1, 0.32, 1) forwards;
        }

        @keyframes tabFadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .glass-card {
            background: rgba(255, 255, 255, 0.8) !important;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.5) !important;
            box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.05) !important;
        }

        .progress-container {
            width: 100%;
            height: 8px;
            background: #e2e8f0;
            border-radius: 99px;
            overflow: hidden;
            margin: 12px 0;
            position: relative;
        }
        .progress-bar {
            height: 100%;
            background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
            border-radius: 99px;
            transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
        }
        .progress-bar::after {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            animation: progressShim 2s infinite;
        }
        @keyframes progressShim {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
        }

        /* Presence Indicator Pulse */
        @keyframes pulsePresence {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
            70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
        .presence-pulse {
            width: 10px;
            height: 10px;
            background: #10b981;
            border-radius: 50%;
            margin-right: 8px;
            position: relative;
        }
        .presence-pulse::after {
            content: '';
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background: #10b981;
            border-radius: 50%;
            animation: pulsePresence 2s infinite;
        }

        .mobile-dock-only { display: none; }
        @media (max-width: 1024px) {
            .dashboard-grid { grid-template-columns: 1fr; }
            .desktop-sidebar-only, .advisor-sidebar { display: none; }
            .mobile-dock-only { display: block; position: fixed; bottom: 0; left: 0; right: 0; z-index: 1000; background: white; border-top: 1px solid #e2e8f0; }
        }

        /* Smart Action Item */
        .smart-action-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: white;
          border: 1px solid #eef2f6;
          border-radius: 16px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .smart-action-item:hover {
          border-color: #3b82f644;
          background: #f8fbff;
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.05);
        }
        .smart-action-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
          flex-shrink: 0;
        }
        .smart-action-item:hover .smart-action-icon {
          background: #3b82f615;
          transform: scale(1.05);
        }

        /* Booking Wizard Card Styles */
        .booking-card {
          background: white;
          border: 1px solid #eef2f6;
          border-radius: 24px;
          padding: 32px 24px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .dark .booking-card { background: #1e293b; border-color: #334155; }
        .booking-card:hover {
          transform: translateY(-8px);
          border-color: #6366f1;
          box-shadow: 0 20px 40px rgba(99, 102, 241, 0.1);
          background: #fdfdff;
        }
        .dark .booking-card:hover { background: #232d45; }
        .booking-card.selected {
          border-color: #6366f1;
          background: #f5f3ff;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
        }
        .dark .booking-card.selected { background: rgba(99, 102, 241, 0.1); }
        .booking-card.selected::after {
            content: '✓';
            position: absolute;
            top: 12px; right: 12px;
            width: 24px; height: 24px;
            background: #6366f1;
            color: white;
            border-radius: 50%;
            display: flex; alignItems: center; justifyContent: center;
            font-size: 0.8rem; fontWeight: 800;
        }

        /* Timeline Styles */
        .timeline-item {
          display: flex;
          gap: 16px;
          padding-bottom: 20px;
          position: relative;
        }
        .timeline-item:not(:last-child)::before {
          content: '';
          position: absolute;
          left: 11px;
          top: 24px;
          bottom: 0;
          width: 2px;
          background: #f1f5f9;
        }
        .timeline-dot {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          border: 2px solid #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          z-index: 1;
          flex-shrink: 0;
        }
        .timeline-content {
          flex: 1;
        }
        .timeline-time {
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .timeline-msg {
          font-size: 0.88rem;
          color: #1e293b;
          line-height: 1.4;
        }

        /* Neon Dark Mode & Premium Effects */
        .dark .glass-card {
          background: rgba(15, 23, 42, 0.8) !important;
          border: 1px solid rgba(99, 102, 241, 0.2) !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 15px rgba(99, 102, 241, 0.1) !important;
        }
        .dark .metric-box {
          background: #0f172a !important;
          border-color: #1e293b !important;
          transition: all 0.3s ease;
        }
        .dark .metric-box:hover {
          border-color: #6366f1 !important;
          box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .dark .smart-action-item {
          background: #1e293b !important;
          border-color: #334155 !important;
        }
        .dark .smart-action-item:hover {
          background: #232d45 !important;
          border-color: #6366f144 !important;
          box-shadow: 0 4px 20px rgba(99, 102, 241, 0.1);
        }
        .dark .timeline-dot {
          background: #0f172a !important;
        }
        .dark .timeline-msg { color: #f1f5f9; }
        .dark .timeline-time { color: #94a3b8; }
        .dark .smart-action-item .smart-action-label { color: #f1f5f9 !important; }
        .dark .smart-action-item .smart-action-desc { color: #94a3b8 !important; }
        .dark .advisor-name { color: #f8fafc !important; }
        .dark .advisor-bio { color: #cbd5e1 !important; background: rgba(30, 41, 59, 0.5) !important; border-color: #334155 !important; }
        .dark .referral-title { color: #f8fafc !important; }
        .dark .referral-desc { color: #94a3b8 !important; }
        .dark .referral-box { background: rgba(15, 23, 42, 0.4) !important; border-color: #334155 !important; }
        .dark .referral-link { color: #f1f5f9 !important; }
        .dark .metric-value { color: #f8fafc !important; }
        .dark .metric-label { color: #94a3b8 !important; }

        /* Recruitment Message Bubble Dark Mode */
        .recruitment-msg-bubble {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }
        .dark .recruitment-msg-bubble {
          background: #1e293b !important;
          border-color: #334155 !important;
        }
        .dark .recruitment-msg-bubble .msg-title { color: #f1f5f9 !important; }
        .dark .recruitment-msg-bubble .msg-text { color: #94a3b8 !important; }

        .advisor-sidebar {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .sidebar-col, .advisor-sidebar {
            position: relative;
        }

        @media (max-width: 1200px) {
           .dashboard-grid { grid-template-columns: 80px 1fr 280px; gap: 20px; } /* Slimmer sidebar if needed, or just standard */
        }
        @media (max-width: 1100px) {
           .dashboard-grid { grid-template-columns: 240px 1fr; }
           .advisor-sidebar { display: none; } /* Hide advisor on smaller desktop, or move to bottom */
        }
        @media (max-width: 900px) {
           .dashboard-grid { block: flex; display: block; }
           .sidebar-col { display: none; } /* Mobile dock takes over */
        }

        /* Mobile/Desktop Visibility */
        .mobile-dock-only { display: none; }
        .desktop-sidebar-only { display: block; }
        
        @media (max-width: 900px) {
           .mobile-dock-only { display: block; }
           .desktop-sidebar-only { display: none; }
        }

        /* Form styling */
        .profile-form label{ font-weight:600; color:var(--text,#111827); }
        .profile-form input{ background:var(--surface,#fff); border:1px solid #e5e7eb; color:var(--text,#111827); border-radius:10px; padding:10px 12px; outline:none; transition: box-shadow .15s ease, border-color .15s ease; }
        .profile-form input::placeholder{ color:#9ca3af; }
        .profile-form input:focus{ border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.18); }
        .dark .profile-form input{ background:#0b1220; border-color:#1f2937; color:#e5e7eb; }
        .dark .profile-form input:focus{ border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.22); }

        /* Buttons */
        .btn{ display:inline-flex; align-items:center; gap:8px; padding:10px 14px; border-radius:10px; border:1px solid #e5e7eb; background:linear-gradient(180deg,#fff,#f8fafc); color:#111827; cursor:pointer; }
        .btn:hover{ filter:brightness(1.02); transform:translateY(-1px); }
        .btn:active{ transform:translateY(0); }
        .btn.success{ border-color:rgba(16,185,129,.45); color:#065f46; background:linear-gradient(135deg,#10b981, #22c55e); color:#fff; }
        .btn.success:hover{ filter:brightness(1.05); }
        .dark .btn{ background:linear-gradient(180deg,#0b1220,#0f172a); border-color:#1f2937; color:#e5e7eb; }
        .dark .btn.success{ background:linear-gradient(135deg,#10b981, #22c55e); border-color:rgba(16,185,129,.6); color:#fff; }

        /* Quote cards & badges (dark-compatible) */
        .quote-card{ background: var(--surface, #fff); border-color: #e5e7eb; }
        .dark .quote-card{ background: #0b1220; border-color: #1f2937; }
        .quote-badge{ background: var(--surface, #fff); border-color: #e5e7eb; }
        .dark .quote-badge{ background: #0b1220; border-color: #1f2937; }

        /* Chat Styles */
        .chat-section { background: #f8fafc; border: 1px solid #e2e8f0; }
        .dark .chat-section { 
          background: #0f172a; 
          border-color: #1e293b; 
          box-shadow: 0 10px 30px rgba(0,0,0,0.4); 
        }

        .chat-header { background: white; border-bottom: 1px solid #e2e8f0; padding: 16px 24px; }
        .dark .chat-header { 
          background: rgba(30, 41, 59, 1); /* Deep slate */
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
        }
        .dark .chat-header h2 { color: #f8fafc; font-size: 1.1rem; }
        .dark .chat-header-role { color: #94a3b8; font-size: 0.85rem; font-weight: 500; }
        .dark .chat-header-quote { 
          color: #cbd5e1; 
          font-style: italic; 
          font-size: 0.9rem; 
          margin-top: 4px;
          opacity: 0.9;
          font-weight: 300;
        }

        .chat-date { background: #e2e8f0; color: #64748b; }
        .dark .chat-date { background: #334155; color: #94a3b8; }
        
        .chat-input-area { background: white; border-top: 1px solid #e2e8f0; }
        .dark .chat-input-area { background: #0f172a; border-color: #1e293b; }
        
        .chat-bubble-me {
          background: linear-gradient(135deg, #6366f1, #4338ca);
          color: white;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
        }
        
        .chat-bubble-them {
          background: white;
          border: 1px solid #f1f5f9;
          color: #1e293b;
          box-shadow: 0 2px 5px rgba(0,0,0,0.03);
        }
        .dark .chat-bubble-them {
          background: #1e293b;
          border-color: #334155;
          color: #f1f5f9;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .chat-input-wrap {
          background: #f1f5f9;
          border: 1px solid transparent;
          transition: all 0.2s ease;
        }
        .chat-input-wrap:focus-within {
          background: white;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        .dark .chat-input-wrap {
          background: #1e293b; /* Slate-800 for contrast against Slate-900 bg */
          border-color: transparent;
        }
        .dark .chat-input-wrap:focus-within {
          background: #334155; /* Slate-700 on focus */
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
        }

        .chat-date-separator {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          color: #94a3b8;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 12px 0;
        }
        .chat-date-line { flex: 1; height: 1px; background: #f1f5f9; }
        .dark .chat-date-line { background: #1f2937; }

        .chat-messages {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
        }
        .dark .chat-messages {
          scrollbar-color: #334155 transparent;
          background: radial-gradient(circle at top right, rgba(99, 102, 241, 0.05), transparent 400px);
        }
        .chat-messages::-webkit-scrollbar { width: 6px; }
        .chat-messages::-webkit-scrollbar-track { background: transparent; }
        .chat-messages::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        .chat-messages::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .dark .chat-messages::-webkit-scrollbar-thumb { background: #334155; }
        .dark .chat-messages::-webkit-scrollbar-thumb:hover { background: #475569; }
        .quote-card:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        .dark .roadmap-step.completed .roadmap-label { color: #f1f5f9; }

        /* Vault Styles */
        .vault-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 20px;
          margin-top: 24px;
        }
        .vault-card {
          background: white;
          border: 1px solid #eef2f6;
          border-radius: 20px;
          padding: 20px;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .dark .vault-card { background: #1e293b; border-color: #334155; }
        .vault-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.06);
          border-color: #6366f144;
        }
        .vault-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }
        .dark .vault-icon { background: #0f172a; }
        .vault-badge {
          font-size: 0.65rem;
          font-weight: 800;
          padding: 4px 8px;
          border-radius: 8px;
          text-transform: uppercase;
        }
        .badge-invoice { background: #ecfdf5; color: #10b981; }
        .badge-contract { background: #eef2ff; color: #6366f1; }
        .badge-quote { background: #fff7ed; color: #f59e0b; }
        .badge-specification { background: #fdf2f8; color: #db2777; }
        .badge-sent { background: #e0f2fe; color: #0369a1; }
        .badge-accepted { background: #ecfdf5; color: #10b981; }
        .badge-signed { background: #ecfdf5; color: #10b981; }
        .badge-rejected { background: #fef2f2; color: #dc2626; }
        .badge-expired { background: #f1f5f9; color: #64748b; }
        .badge-paid { background: #ecfdf5; color: #10b981; }
        .badge-pending { background: #fff7ed; color: #ea580c; }
        .badge-overdue { background: #fef2f2; color: #dc2626; }
        .badge-partial { background: #eef2ff; color: #4f46e5; }
        .dark .badge-invoice { background: rgba(16, 185, 129, 0.1); }
        .dark .badge-contract { background: rgba(99, 102, 241, 0.1); }
        .dark .badge-quote { background: rgba(245, 158, 11, 0.1); }
        .dark .badge-specification { background: rgba(219, 39, 119, 0.1); }
        .dark .badge-sent { background: rgba(3, 105, 161, 0.1); }
        .dark .badge-accepted { background: rgba(16, 185, 129, 0.1); }
        .dark .badge-signed { background: rgba(16, 185, 129, 0.1); }
        .dark .badge-rejected { background: rgba(220, 38, 38, 0.1); }
        .dark .badge-expired { background: rgba(100, 116, 139, 0.1); }
        .dark .badge-paid { background: rgba(16, 185, 129, 0.1); }
        .dark .badge-pending { background: rgba(234, 88, 12, 0.1); }
        .dark .badge-overdue { background: rgba(220, 38, 38, 0.1); }
        .dark .badge-partial { background: rgba(79, 70, 229, 0.1); }

        .cat-pill {
          padding: 8px 16px;
          border-radius: 12px;
          background: #f1f5f9;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s;
        }
        .dark .cat-pill { background: #1e293b; color: #94a3b8; }
        .cat-pill.active {
          background: #6366f1;
          color: white;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }
        /* Roadmap Styles */
        .roadmap-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-top: 32px;
          position: relative;
          padding: 0 10px;
        }
        .roadmap-line {
          position: absolute;
          top: 15px;
          left: 40px;
          right: 40px;
          height: 3px;
          background: #e2e8f0;
          z-index: 0;
        }
        .dark .roadmap-line { background: #334155; }
        .roadmap-line-progress {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: linear-gradient(90deg, #6366f1, #3b82f6);
          transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .roadmap-step {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          width: 80px;
        }
        .roadmap-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: white;
          border: 2px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          color: #94a3b8;
          transition: all 0.4s ease;
        }
        .dark .roadmap-circle { background: #1e293b; border-color: #334155; }
        .roadmap-step.active .roadmap-circle {
          border-color: #6366f1;
          color: #6366f1;
          box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
          transform: scale(1.1);
        }
        .roadmap-step.completed .roadmap-circle {
          background: #6366f1;
          border-color: #6366f1;
          color: white;
        }
        .roadmap-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #64748b;
          text-align: center;
          transition: color 0.3s;
        }
        .dark .roadmap-label { color: #94a3b8; }
        .roadmap-step.active .roadmap-label { color: #6366f1; }
        .roadmap-step.completed .roadmap-label { color: #1e293b; }
        .dark .roadmap-step.completed .roadmap-label { color: #f1f5f9; }
      `}</style>

            {/* Tabs above metrics (centered) - restored */}
            <ClientTabs counts={{ projects: pendingProjects, quotes: pendingQuotes, invoices: unpaidInvoices, meetings: pendingMeetings, messages: unreadMessages, contracts: signedContractsCount }} />

            {/* Welcome and metrics shown only on Accueil */}
            {activeSection === 'accueil' && application && (
              <div style={{ marginBottom: 20 }}>
                <div className="card glass-card" style={{ border: '1px solid #10b98133', borderRadius: 16, padding: 24, background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(255,255,255,0.8) 100%) !important' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#065f46', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                      Suivi de votre candidature
                    </h3>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      background: application.status === 'pending' ? '#f59e0b1a' : application.status === 'approved' ? '#10b9811a' : '#ef44441a',
                      color: application.status === 'pending' ? '#d97706' : application.status === 'approved' ? '#059669' : '#dc2626',
                      padding: '4px 12px',
                      borderRadius: 20,
                      textTransform: 'uppercase'
                    }}>
                      {application.status === 'pending' ? 'En attente' : application.status === 'approved' ? 'Acceptée' : 'Refusée'}
                    </span>
                  </div>

                  <p style={{ color: '#4b5563', margin: '0 0 20px 0' }}>
                    Votre dossier pour le rôle de <strong>{application.step.split('_')[1].toUpperCase()}</strong> est actuellement en cours d'examen par notre équipe. Nous reviendrons vers vous très prochainement.
                  </p>

                  <div className="recruitment-msg-bubble">
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#3b82f61a', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="msg-title" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Message de Léon</div>
                      <div className="msg-text" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        "Merci pour votre intérêt, je regarde votre profil d'ici 48h."
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Welcome and metrics shown only on Accueil */}
            {activeSection === 'accueil' && (
              <div style={{ display: 'grid', gridTemplateColumns: activeProject ? '1.5fr 1fr' : '1fr', gap: 24, marginTop: 16 }}>
                <div className="card glass-card" style={{ borderRadius: 24, padding: 32, background: 'var(--card-bg, #fff)' }}>
                  <div style={{ marginBottom: 32 }}>
                    <div className="welcome-title-text" style={{ fontWeight: 800, fontSize: '1.8rem', marginBottom: 8, color: 'var(--text-primary, #1e293b)' }}>Bonjour {user?.name?.split(' ')[0] || 'à vous'} 👋</div>
                    <div className="welcome-subtitle-text" style={{ color: 'var(--muted, #64748b)', fontSize: '1.05rem' }}>Ravi de vous revoir ! Voici un aperçu de vos activités.</div>
                    <style>{`
                        .dark .welcome-title-text { color: #f8fafc !important; }
                        .dark .welcome-subtitle-text { color: #cbd5e1 !important; }
                    `}</style>
                  </div>

                  {/* Smart Actions Checklist */}
                  <div style={{ marginBottom: 40 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
                      Actions recommandées
                    </div>
                    {smartActions.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {smartActions.map(action => (
                          <div
                            key={action.id}
                            className="smart-action-item premium-action-card"
                            onClick={() => action.path && navigate(action.path)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="action-icon-box">{action.icon}</div>
                            <div style={{ flex: 1 }}>
                              <div className="action-label" style={{ fontWeight: 700, color: 'var(--text-primary, #1e293b)' }}>{action.label}</div>
                              <div className="action-desc" style={{ fontSize: '0.9rem', color: 'var(--muted, #64748b)' }}>{action.desc}</div>
                            </div>
                            {action.path && (
                              <div style={{ color: '#3b82f6', fontWeight: 800, fontSize: '1.2rem' }}>→</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="premium-action-card success-state" style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        padding: 24,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 20
                      }}>
                        <div style={{
                          fontSize: '2.5rem',
                          background: 'white',
                          borderRadius: '50%',
                          width: 60, height: 60,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                        }}>
                          ✨
                        </div>
                        <div>
                          <div className="success-title-text" style={{ fontWeight: 800, fontSize: '1.1rem', color: '#065f46', marginBottom: 4 }}>Tout est à jour !</div>
                          <div className="success-desc-text" style={{ fontSize: '0.9rem', color: '#047857', lineHeight: 1.5 }}>
                            Aucune action requise pour le moment.<br />Votre dossier est parfaitement suivi.
                          </div>
                        </div>
                        <style>{`
                            .dark .success-state {
                                background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(6, 95, 70, 0.3) 100%) !important;
                                border-color: rgba(16, 185, 129, 0.4) !important;
                            }
                            .dark .success-title-text { color: #f0fdf4 !important; text-shadow: 0 2px 4px rgba(0,0,0,0.5); }
                            .dark .success-desc-text { color: #bbf7d0 !important; }
                            .dark .success-state div[style*="background: 'white'"] { /* Selector for the emoji bg */
                                background: rgba(255,255,255,0.1) !important;
                                color: white;
                            }
                        `}</style>
                      </div>
                    )}
                  </div>

                  {/* Key Metrics Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                    {/* Projets */}
                    <Link to="/mon-compte/projets" className="premium-stat-card" style={{ '--accent-color': '#10b981' }}>
                      <div className="stat-icon-box" style={{ color: '#10b981', background: '#10b98115' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="stat-label">Projets</div>
                        <div className="stat-value">{pendingProjects}</div>
                      </div>
                      <div className="stat-arrow">→</div>
                    </Link>

                    {/* Devis */}
                    <Link to="/mon-compte/devis" className="premium-stat-card" style={{ '--accent-color': '#f59e0b' }}>
                      <div className="stat-icon-box" style={{ color: '#f59e0b', background: '#f59e0b15' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="stat-label">Devis</div>
                        <div className="stat-value">{pendingQuotes}</div>
                      </div>
                      <div className="stat-arrow">→</div>
                    </Link>

                    {/* Impayés */}
                    <Link to="/mon-compte/factures" className="premium-stat-card" style={{ '--accent-color': '#ec4899' }}>
                      <div className="stat-icon-box" style={{ color: '#ec4899', background: '#ec489915' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="stat-label">Impayés</div>
                        <div className="stat-value">{unpaidInvoices}</div>
                      </div>
                      <div className="stat-arrow">→</div>
                    </Link>
                  </div>

                  {/* Activity Timeline Section - Minimalist */}
                  <div style={{ marginTop: 24 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#94a3b8', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#94a3b8' }}></div>
                      ACTIVITÉ RÉCENTE
                    </div>
                    <div className="minimal-timeline" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {timeline.length > 0 ? timeline.slice(0, 3).map((item, idx) => (
                        <div key={item.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                          <div style={{
                            minWidth: 8, height: 8, borderRadius: '50%',
                            background: item.type === 'message' ? '#3b82f6' : '#10b981',
                            boxShadow: item.type === 'message' ? '0 0 8px rgba(59, 130, 246, 0.4)' : '0 0 8px rgba(16, 185, 129, 0.4)'
                          }}></div>
                          <div style={{ flex: 1, fontSize: '0.9rem', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.message || 'Activité sur votre compte'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                            {getDayLabel(item.time)}
                          </div>
                        </div>
                      )) : (
                        <div style={{ color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic', paddingLeft: 4 }}>
                          Rien à signaler pour le moment.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Referral Card (Step 5) */}
                  {/* Referral Section with VIP Card */}
                  <div style={{ marginTop: 32 }}>

                    {/* 1. Context: Title & Desc */}
                    <div style={{ marginBottom: 20, textAlign: 'center' }}>
                      <div className="referral-title" style={{ fontSize: '1.4rem', fontWeight: 900, marginBottom: 8, color: '#1e293b' }}>
                        Partagez l'expérience InfoColab
                      </div>
                      <p className="referral-desc" style={{ color: '#64748b', margin: '0 auto', maxWidth: '500px', lineHeight: 1.6 }}>
                        Faites profiter vos partenaires de notre expertise et recevez une prime de parrainage sur chaque projet validé.
                      </p>
                    </div>

                    {/* 2. Visual: 3D Interactive VIP Card */}
                    <div className="vip-card-scene" style={{ perspective: '1200px', maxWidth: 400, margin: '0 auto 24px', height: 240, position: 'relative' }}>
                      <div
                        className="vip-card-3d"
                        onMouseDown={(e) => {
                          setIsCardDragging(true);
                          setDragStart({ x: e.clientX, y: e.clientY });
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'relative',
                          transformStyle: 'preserve-3d',
                          transform: `rotateX(${cardRotation.x}deg) rotateY(${cardRotation.y}deg)`,
                          transition: isCardDragging ? 'none' : 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)', // Elastic bounce back if wanted, or smooth
                          cursor: isCardDragging ? 'grabbing' : 'grab'
                        }}
                      >
                        {/* RECTO (FRONT) */}
                        <div className="vip-card-face vip-card-front" style={{
                          position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
                          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
                          borderRadius: 16, padding: 24, overflow: 'hidden',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.4)',
                          display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                        }}>
                          {/* Front Content (Same as before) */}
                          <div style={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }}></div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
                            <div>
                              <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'sans-serif', letterSpacing: '0.05em', color: 'white' }}>InfoColab</div>
                              <div style={{ fontSize: '0.6rem', fontWeight: 600, fontFamily: 'sans-serif', letterSpacing: '0.2em', opacity: 0.7, color: 'white' }}>AMBASSADOR</div>
                            </div>
                            <svg width="40" height="30" viewBox="0 0 50 35" fill="none" style={{ opacity: 0.9 }}>
                              <rect width="50" height="35" rx="4" fill="#fbbf24" fillOpacity="0.2" stroke="#fbbf24" strokeWidth="0.5" />
                              <path d="M10 0V35" stroke="#fbbf24" strokeOpacity="0.5" />
                              <path d="M40 0V35" stroke="#fbbf24" strokeOpacity="0.5" />
                            </svg>
                          </div>

                          <div style={{ position: 'relative', zIndex: 1, margin: '20px 0', pointerEvents: 'none' }}>
                            <div style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: 4, fontFamily: 'sans-serif', color: 'white' }}>CODE PARRAINAGE</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 600, letterSpacing: '0.1em', textShadow: '0 2px 4px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 10, fontFamily: "'Courier New', monospace", color: 'white' }}>
                              {user?.id?.substring(0, 4) || 'XXXX'} •••• •••• {(user?.id?.substring(user.id.length - 4)) || 'XXXX'}
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
                            <div>
                              <div style={{ fontSize: '0.6rem', opacity: 0.6, marginBottom: 2, fontFamily: 'sans-serif', color: 'white' }}>TITULAIRE</div>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'white' }}>{user?.name || 'MEMBRE'}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.6rem', opacity: 0.6, marginBottom: 2, fontFamily: 'sans-serif', color: 'white' }}>MEMBRE DEPUIS</div>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'white' }}>2024</div>
                            </div>
                          </div>
                          <div className="card-shine"></div>
                        </div>

                        {/* VERSO (BACK) */}
                        <div className="vip-card-face vip-card-back" style={{
                          position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                          background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
                          borderRadius: 16, overflow: 'hidden',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.4)',
                          display: 'flex', flexDirection: 'column'
                        }}>
                          {/* Magnetic Strip */}
                          <div style={{ height: 40, background: '#000', marginTop: 20, width: '100%' }}></div>

                          <div style={{ padding: '10px 24px', display: 'flex', gap: 20, alignItems: 'center', flex: 1 }}>
                            {/* Signature Panel */}
                            <div style={{ flex: 1 }}>
                              <div style={{ height: 30, background: '#fff', opacity: 0.8, marginBottom: 8, display: 'flex', alignItems: 'center', paddingLeft: 10, fontFamily: 'Caveat, cursive', color: '#000', fontSize: '1.2rem' }}>
                                {user?.name}
                              </div>
                              <div style={{ fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1.2 }}>
                                Carte de membre exclusive InfoColab.<br />Non cessible. Usage réservé.
                              </div>
                            </div>

                            {/* QR Code */}
                            <div style={{ width: 80, height: 80, background: 'white', padding: 4, borderRadius: 4 }}>
                              <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${window.location.origin}/onboarding?ref=${user?.id?.substring(0, 8)}`)}`}
                                alt="QR"
                                style={{ width: '100%', height: '100%' }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 3. Action: Clear Copy Link */}
                    <div className="referral-box" style={{ display: 'flex', gap: 12, alignItems: 'center', background: '#f8fafc', padding: 12, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                      <div className="referral-link" style={{ flex: 1, fontSize: '0.85rem', color: '#334155', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {window.location.origin}/onboarding?ref={user?.id?.substring(0, 8)}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/onboarding?ref=${user?.id?.substring(0, 8)}`);
                          showToast('Lien de parrainage copié !', 'success');
                        }}
                        className="btn"
                        style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', fontWeight: 700, fontSize: '0.85rem' }}
                      >
                        Copier
                      </button>
                    </div>

                    <style>{`
                      .vip-card { transition: transform 0.3s ease; }
                      .vip-card:hover { transform: translateY(-5px) scale(1.02); }
                      .card-shine {
                        position: absolute; top: 0; left: 0; right: 0; bottom: 0;
                        background: linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.05) 25%, transparent 30%);
                        transform: translateX(-100%);
                        animation: shine 6s infinite linear;
                        pointer-events: none;
                      }
                      @keyframes shine {
                        0% { transform: translateX(-100%); }
                        20% { transform: translateX(100%); }
                        100% { transform: translateX(100%); }
                      }
                      .dark .referral-title { color: #f8fafc !important; }
                      .dark .referral-desc { color: #94a3b8 !important; }
                      .dark .referral-box { background: #0f172a !important; border-color: rgba(99, 102, 241, 0.4) !important; color: #f8fafc; }
                      .dark .referral-link { color: #f8fafc !important; }
                    `}</style>
                  </div>
                </div>

                {activeProject && (
                  <div className="card glass-card" style={{ borderRadius: 16, padding: 24, border: '1px solid #3b82f633', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(255,255,255,0.8) 100%) !important' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem' }}>Suivi de projet</div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, background: '#3b82f61a', color: '#3b82f6', padding: '4px 10px', borderRadius: 20 }}>ACTIF</span>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>{activeProject.name}</div>

                    <div className="roadmap-container">
                      <div className="roadmap-line">
                        <div className="roadmap-line-progress" style={{ width: `${Math.min(100, Math.max(0, (activeProjectProgress / 100) * 100))}%` }}></div>
                      </div>

                      {[
                        { id: 1, label: 'Audit', icon: '🔍', threshold: 0 },
                        { id: 2, label: 'Conception', icon: '🎨', threshold: 30 },
                        { id: 3, label: 'Production', icon: '⚡', threshold: 60 },
                        { id: 4, label: 'Lancement', icon: '🚀', threshold: 90 }
                      ].map((step, idx) => {
                        const isCompleted = activeProjectProgress > step.threshold + 20;
                        const isActive = activeProjectProgress >= step.threshold && activeProjectProgress <= step.threshold + 30;

                        return (
                          <div key={step.id} className={`roadmap-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                            <div className="roadmap-circle">
                              {isCompleted ? '✓' : step.icon}
                            </div>
                            <div className="roadmap-label">{step.label}</div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                        Progression globale
                      </div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#6366f1' }}>
                        {activeProjectProgress}%
                      </div>
                    </div>
                    <Link to="/mon-compte/projets" className="btn" style={{ width: '100%', marginTop: 16, textAlign: 'center', background: '#3b82f6', color: 'white', border: 'none' }}>
                      Détails du projet
                    </Link>
                  </div>
                )}
              </div>
            )}



            {activeSection === 'accueil' && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#94a3b8', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Navigation rapide
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
                  {[
                    {
                      to: '/mon-compte',
                      title: 'Accueil',
                      desc: 'Vue d’ensemble et indicateurs.',
                      color: '#6366f1',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                    },
                    {
                      to: '/mon-compte/profil',
                      title: 'Profil',
                      desc: 'Gérez vos informations personnelles.',
                      color: '#8b5cf6',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    },
                    {
                      to: '/mon-compte/projets',
                      title: 'Projets',
                      desc: 'Consultez vos projets et leur statut.',
                      color: '#10b981',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                    },
                    {
                      to: '/mon-compte/devis',
                      title: 'Devis',
                      desc: 'Liste, suivi et validation.',
                      color: '#f59e0b',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    },
                    {
                      to: '/mon-compte/factures',
                      title: 'Factures',
                      desc: 'Consultation et téléchargement.',
                      color: '#ec4899',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>
                    },
                    {
                      to: '/mon-compte/coffre-fort',
                      title: 'Coffre-fort',
                      desc: 'Documents et fichiers sécurisés.',
                      color: '#06b6d4',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    },
                    {
                      to: '/mon-compte/rendez-vous',
                      title: 'Rendez-vous',
                      desc: 'Listes et prochains meetings.',
                      color: '#3b82f6',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    },
                    {
                      to: '/mon-compte/contact',
                      title: 'Contact',
                      desc: 'Contactez notre équipe.',
                      color: '#64748b',
                      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                    }
                  ].map((item, idx) => (
                    <Link key={idx} to={item.to} className="premium-nav-card" style={{ '--hover-color': item.color, flex: '1 1 240px', maxWidth: '340px' }}>
                      <div className="nav-card-icon" style={{ color: item.color, background: `${item.color}15`, padding: 12, borderRadius: 12, width: 'fit-content' }}>
                        {item.icon}
                      </div>
                      <div>
                        <div className="premium-nav-title">{item.title}</div>
                        <div className="premium-nav-desc">{item.desc}</div>
                      </div>
                      <div className="premium-nav-arrow" style={{ color: item.color }}>→</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Recruitment Section - Adaptive (CTA only if NOT applied) */}
            {activeSection === 'accueil' && !application && (
              <div style={{ marginTop: 24 }}>
                <div className="card" style={{ border: '1px solid #3b82f6', borderRadius: 16, padding: 24, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)' }}>
                  <h3 style={{ marginTop: 0, color: 'white', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    Rejoindre l'équipe
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.9)', maxWidth: 600, fontSize: '0.95rem' }}>
                    Vous souhaitez devenir partenaire ? Postulez en quelques clics via votre compte existant.
                  </p>
                  <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
                    <button className="btn" style={{ border: 'none', background: 'white', color: '#1e40af', fontWeight: 600, padding: '10px 20px' }} onClick={() => navigate('/candidature', {
                      state: {
                        role: 'commercial',
                        skipIntro: true,
                        staff: {
                          firstName: user?.name?.split(' ')[0] || '',
                          lastName: user?.name?.split(' ').slice(1).join(' ') || '',
                          email: user?.email || '',
                          phone: customer?.phone || ''
                        }
                      }
                    })}>
                      Devenir Commercial
                    </button>
                    <button className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', padding: '10px 20px' }} onClick={() => navigate('/candidature', {
                      state: {
                        role: 'developpeur',
                        skipIntro: true,
                        staff: {
                          firstName: user?.name?.split(' ')[0] || '',
                          lastName: user?.name?.split(' ').slice(1).join(' ') || '',
                          email: user?.email || '',
                          phone: customer?.phone || ''
                        }
                      }
                    })}>
                      Devenir Développeur
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tab content (no outer card) with animation */}
            <div style={{ marginTop: 12 }} key={activeSection} className="tab-content-anim">
              {activeSection === 'profil' && (
                <ClientProfileForm user={user} customer={customer} setCustomer={setCustomer} application={application} />
              )}
              {activeSection === 'projets' && (
                <div>
                  <h2 style={{ marginTop: 0 }}>Projets</h2>
                  {projectsLoading ? (
                    <div>Chargement…</div>
                  ) : projects.length === 0 ? (
                    <div className="premium-empty-state" style={{ textAlign: 'center', padding: '60px 20px', borderRadius: 24 }}>
                      <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.8 }}>🚀</div>
                      <div style={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 8 }} className="empty-title">Aucun projet en cours</div>
                      <p style={{ fontSize: '0.9rem', opacity: 0.7, margin: 0 }} className="empty-desc">Vos projets actifs apparaîtront ici.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 12 }}>
                      {projects.map((p, index) => (
                        <div key={p.id}>
                          <ClientProjectCard project={p} isDefaultExpanded={index === 0} />
                          <ClientProjectSpecifications projectId={p.id} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {activeSection === 'devis' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ marginTop: 0 }}>Mes Devis</h2>
                    <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>{quotes.length} document{quotes.length > 1 ? 's' : ''}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '16px 0 24px' }}>
                    {[
                      { id: 'all', label: 'Tous' },
                      { id: 'sent', label: 'Envoyés' },
                      { id: 'accepted', label: 'Acceptés' },
                      { id: 'rejected', label: 'Refusés' },
                      { id: 'expired', label: 'Expirés' }
                    ].map(f => (
                      <div
                        key={f.id}
                        className={`cat-pill ${quoteFilter === f.id ? 'active' : ''}`}
                        onClick={() => setQuoteFilter(f.id)}
                      >
                        {f.label}
                      </div>
                    ))}
                  </div>

                  {loadingTab ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>Chargement…</div>
                  ) : (
                    <div className="vault-grid">
                      {quotes.filter(q => {
                        if (quoteFilter === 'all') return true;
                        if (quoteFilter === 'expired') return isExpired(q) && (q.status !== 'accepted');
                        return (q.status === quoteFilter);
                      }).length === 0 ? (
                        <div style={{ gridColumn: '1/-1', padding: '60px 20px', textAlign: 'center', borderRadius: 24 }} className="premium-empty-state">
                          <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.8 }}>🖊️</div>
                          <div style={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 8 }} className="empty-title">Aucun devis trouvé</div>
                          <p style={{ fontSize: '0.9rem', opacity: 0.7 }} className="empty-desc">Les devis correspondants s'afficheront ici.</p>
                        </div>
                      ) : (
                        quotes.filter(q => {
                          if (quoteFilter === 'all') return true;
                          if (quoteFilter === 'expired') return isExpired(q) && (q.status !== 'accepted');
                          return (q.status === quoteFilter);
                        }).map(q => {
                          const expired = isExpired(q) && q.status !== 'accepted' && q.status !== 'signed';
                          const status = expired ? 'expired' : q.status;

                          return (
                            <div key={q.id} className="vault-card">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div className="vault-icon">
                                  {status === 'accepted' || status === 'signed' ? '✅' : status === 'rejected' ? '❌' : status === 'expired' ? '⏳' : '🖊️'}
                                </div>
                                <span className={`vault-badge badge-${status}`}>
                                  {statusLabel(status)}
                                </span>
                              </div>

                              <div>
                                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }} className="dark:text-white">
                                  {q.quote_number || 'Devis sans numéro'}
                                </div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#6366f1', marginTop: 4 }}>
                                  {typeof q.total === 'number' ? q.total.toFixed(2) : '0.00'}€ <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8' }}>TTC</span>
                                </div>
                                {q.valid_until && (
                                  <div style={{ fontSize: '0.8rem', color: expired ? '#dc2626' : '#64748b', marginTop: 8, fontWeight: expired ? 700 : 500 }}>
                                    {expired ? 'Expiré le ' : 'Valide jusqu\'au '} {new Date(q.valid_until).toLocaleDateString()}
                                  </div>
                                )}
                              </div>

                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {q?.deposit_config?.enabled && (
                                  <div style={{ fontSize: '0.7rem', padding: '4px 8px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
                                    💰 Acompte {q.deposit_config.type === 'percent' ? `${q.deposit_config.value}%` : `${q.deposit_config.value}€`}
                                  </div>
                                )}
                                {Number(q?.installments_count || 0) > 1 && (
                                  <div style={{ fontSize: '0.7rem', padding: '4px 8px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
                                    📅 {q.installments_count}x {q.installments_frequency}
                                  </div>
                                )}
                              </div>

                              <div style={{ marginTop: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <button className="btn" style={{ fontSize: '0.8rem', padding: '8px', justifyContent: 'center' }} onClick={() => handleQuotePDF(q, 'preview')}>Voir</button>
                                <button className="btn" style={{ fontSize: '0.8rem', padding: '8px', justifyContent: 'center' }} onClick={() => handleQuotePDF(q, 'download')}>PDF</button>

                                {(q.status === 'accepted' || q.status === 'signed') ? (
                                  <button className="btn success" style={{ gridColumn: '1/-1', fontSize: '0.8rem', padding: '8px', justifyContent: 'center' }} onClick={() => handleContractPDF(q)}>
                                    📜 Voir le Contrat
                                  </button>
                                ) : (
                                  q.status !== 'rejected' && !expired && (
                                    <>
                                      <button className="btn success" style={{ fontSize: '0.8rem', padding: '8px', justifyContent: 'center' }} onClick={() => { setSelectedQuote(q); setConsent(false); setSigName(user?.name || user?.email || ''); }}>Valider</button>
                                      <button className="btn" style={{ fontSize: '0.8rem', padding: '8px', justifyContent: 'center' }} onClick={() => rejectQuote(q)}>Refuser</button>
                                    </>
                                  )
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
              {activeSection === 'factures' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ marginTop: 0 }}>Mes Factures</h2>
                    <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>{invoices.length} document{invoices.length > 1 ? 's' : ''}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '16px 0 24px' }}>
                    {[
                      { id: 'all', label: 'Toutes' },
                      { id: 'pending', label: 'En attente' },
                      { id: 'partial', label: 'Partiel' },
                      { id: 'overdue', label: 'En retard' },
                      { id: 'paid', label: 'Payées' }
                    ].map(f => (
                      <div
                        key={f.id}
                        className={`cat-pill ${invoiceFilter === f.id ? 'active' : ''}`}
                        onClick={() => setInvoiceFilter(f.id)}
                      >
                        {f.label}
                      </div>
                    ))}
                  </div>

                  {loadingTab ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>Chargement…</div>
                  ) : (
                    <div className="vault-grid">
                      {invoices.filter(inv => {
                        if (invoiceFilter === 'all') return true;
                        return (inv.payment_status === invoiceFilter);
                      }).length === 0 ? (
                        <div style={{ gridColumn: '1/-1', padding: '60px 20px', textAlign: 'center', borderRadius: 24 }} className="premium-empty-state">
                          <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.8 }}>📄</div>
                          <div style={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 8 }} className="empty-title">Aucune facture trouvée</div>
                          <p style={{ fontSize: '0.9rem', opacity: 0.7 }} className="empty-desc">Vos factures apparaîtront ici.</p>
                        </div>
                      ) : (
                        invoices.filter(inv => {
                          if (invoiceFilter === 'all') return true;
                          return (inv.payment_status === invoiceFilter);
                        }).map(inv => {
                          const status = inv.payment_status || 'pending';
                          const statusLabelText = { paid: 'Payée', pending: 'En attente', partial: 'Partiel', overdue: 'En retard' }[status] || 'En attente';

                          return (
                            <div key={inv.id} className="vault-card">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div className="vault-icon">
                                  {status === 'paid' ? '💰' : status === 'overdue' ? '⚠️' : status === 'partial' ? '🌓' : '🕒'}
                                </div>
                                <span className={`vault-badge badge-${status}`}>
                                  {statusLabelText}
                                </span>
                              </div>

                              <div>
                                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }} className="dark:text-white">
                                  {inv.invoice_number || 'Facture sans numéro'}
                                </div>
                                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#10b981', marginTop: 4 }}>
                                  {typeof inv.total === 'number' ? inv.total.toFixed(2) : '0.00'}€ <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8' }}>TTC</span>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 8 }}>
                                  Émise le {new Date(inv.created).toLocaleDateString()}
                                </div>
                              </div>

                              <div style={{ marginTop: 'auto', display: 'grid', gridTemplateColumns: status === 'paid' ? '1fr 1fr' : '1fr 1fr 1fr', gap: 6 }}>
                                <button className="btn" style={{ fontSize: '0.75rem', padding: '8px 4px', justifyContent: 'center' }} onClick={() => handleInvoicePDF(inv, 'preview')}>Voir</button>
                                <button className="btn" style={{ fontSize: '0.75rem', padding: '8px 4px', justifyContent: 'center' }} onClick={() => handleInvoicePDF(inv, 'download')}>PDF</button>
                                {status !== 'paid' && (
                                  <button className="btn success" style={{ fontSize: '0.75rem', padding: '8px 4px', justifyContent: 'center' }} onClick={() => alert('Paiement en ligne bientôt disponible.')}>Payer</button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeSection === 'contrats' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ marginTop: 0 }}>Mes Contrats</h2>
                    <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>{contracts.length} document{contracts.length > 1 ? 's' : ''}</span>
                  </div>

                  {loadingTab ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>Chargement…</div>
                  ) : (
                    <div className="vault-grid">
                      {contracts.length === 0 ? (
                        <div style={{ gridColumn: '1/-1', padding: '60px 20px', textAlign: 'center', borderRadius: 24 }} className="premium-empty-state">
                          <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.8 }}>📜</div>
                          <div style={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 8 }} className="empty-title">Aucun contrat trouvé</div>
                          <p style={{ fontSize: '0.9rem', opacity: 0.7 }} className="empty-desc">Vos contrats signés apparaîtront ici.</p>
                        </div>
                      ) : (
                        contracts.map(c => (
                          <div key={c.id} className="vault-card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div className="vault-icon">📜</div>
                              <span className={`vault-badge badge-contract`}>
                                {c.status === 'signed' ? 'Signé' : 'À signer'}
                              </span>
                            </div>

                            <div>
                              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }} className="dark:text-white">
                                {c.title || `Contrat #${c.id.substring(0, 6)}`}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 8 }}>
                                {c.signed_at
                                  ? `Signé le ${new Date(c.signed_at).toLocaleDateString()}`
                                  : `Créé le ${new Date(c.created).toLocaleDateString()}`
                                }
                              </div>
                            </div>

                            <div style={{ marginTop: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <button className="btn" style={{ fontSize: '0.8rem', padding: '8px', justifyContent: 'center' }} onClick={() => previewContractPDF(c)}>Voir</button>
                              <button className="btn success" style={{ fontSize: '0.8rem', padding: '8px', justifyContent: 'center' }} onClick={() => downloadContractPDF(c)}>Télécharger</button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              {activeSection === 'coffre-fort' && (
                <div className="tab-content-anim">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Coffre-fort Numérique</h2>
                      <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '0.9rem' }}>Tous vos documents officiels et livrables sécurisés.</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
                    {[
                      { id: 'all', label: 'Tout' },
                      { id: 'invoice', label: 'Factures' },
                      { id: 'contract', label: 'Contrats' },
                      { id: 'quote', label: 'Devis' },
                      { id: 'specification', label: 'Cahier des charges' }
                    ].map(cat => (
                      <div
                        key={cat.id}
                        className={`cat-pill ${vaultFilter === cat.id ? 'active' : ''}`}
                        onClick={() => setVaultFilter(cat.id)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {allDocuments.filter(d => vaultFilter === 'all' || d.vaultType === vaultFilter).length > 0 ? (
                    <div className="vault-grid">
                      {allDocuments.filter(d => vaultFilter === 'all' || d.vaultType === vaultFilter).map(doc => (
                        <div key={`${doc.vaultType}-${doc.id}`} className="vault-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div className="vault-icon">
                              {doc.vaultType === 'invoice' ? '📄' : doc.vaultType === 'contract' ? '📜' : doc.vaultType === 'specification' ? '📋' : '🖊️'}
                            </div>
                            <span className={`vault-badge badge-${doc.vaultType}`}>
                              {doc.vaultType === 'invoice' ? 'Facture' : doc.vaultType === 'contract' ? 'Contrat' : doc.vaultType === 'specification' ? 'Cahier des charges' : 'Devis'}
                            </span>
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }} className="dark:text-white">{doc.label}</div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>Ajouté le {new Date(doc.date).toLocaleDateString()}</div>
                          </div>
                          <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
                            <button
                              className="btn"
                              style={{ flex: 1, fontSize: '0.8rem', padding: '8px' }}
                              onClick={() => {
                                if (doc.vaultType === 'invoice') handleInvoicePDF(doc);
                                if (doc.vaultType === 'contract') previewContractPDF(doc);
                                if (doc.vaultType === 'quote') handleQuotePDF(doc);
                                if (doc.vaultType === 'specification') setSelectedSpecId(doc.expand?.project?.id || doc.project);
                              }}
                            >
                              Voir
                            </button>
                            <button
                              className="btn success"
                              style={{ flex: 1, fontSize: '0.8rem', padding: '8px', display: doc.vaultType === 'specification' ? 'none' : 'block' }}
                              onClick={() => {
                                if (doc.vaultType === 'invoice') handleInvoicePDF(doc, 'download');
                                if (doc.vaultType === 'contract') downloadContractPDF(doc);
                                if (doc.vaultType === 'quote') handleQuotePDF(doc, 'download');
                              }}
                            >
                              PDF
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '80px 20px', textAlign: 'center', borderRadius: 24 }} className="premium-empty-state">
                      <div style={{ fontSize: '3rem', marginBottom: 16 }}>📁</div>
                      <div style={{ fontWeight: 700 }} className="empty-title">Aucun document trouvé</div>
                      <p style={{ fontSize: '0.9rem' }} className="empty-desc">Les fichiers apparaîtront ici dès qu'ils seront disponibles.</p>
                    </div>
                  )}
                </div>
              )}
              {/* Specification Modal */}
              {selectedSpecId && (
                <div style={{
                  position: 'fixed', inset: 0, zIndex: 10000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                  padding: 20
                }} onClick={() => setSelectedSpecId(null)}>
                  <div style={{
                    background: 'var(--card-bg, #fff)', width: '95%', maxWidth: 900,
                    maxHeight: '90vh', overflowY: 'auto', borderRadius: 24, padding: 32,
                    position: 'relative', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
                  }} onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedSpecId(null)}
                      style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}
                    >
                      ✕
                    </button>
                    <ClientProjectSpecifications projectId={selectedSpecId} />
                  </div>
                </div>
              )}

              {
                activeSection === 'rendez-vous' && (
                  <div className="tab-content-anim">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                      <div>
                        <h2 style={{ margin: 0 }}>Rendez-vous</h2>
                        <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '0.9rem' }}>
                          {showBookingWizard ? "Planification de votre prochain échange" : "Gérez vos échanges avec l'équipe InfoColab."}
                        </p>
                      </div>
                      {!showBookingWizard && (
                        <button
                          className="btn success hover-scale"
                          onClick={() => {
                            setShowBookingWizard(true);
                            setBookingStep(1);
                          }}
                          style={{ borderRadius: 12, padding: '12px 24px', fontWeight: 700, boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)' }}
                        >
                          🗓️ Nouveau Rendez-vous
                        </button>
                      )}
                      {showBookingWizard && (
                        <button
                          className="btn"
                          onClick={() => setShowBookingWizard(false)}
                          style={{ borderRadius: 12, padding: '10px 16px' }}
                        >
                          Annuler
                        </button>
                      )}
                    </div>

                    {showBookingWizard ? (
                      <div className="glass-card" style={{ padding: 32, borderRadius: 24, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                        {/* Wizard Progress Bar */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 40 }}>
                          {[1, 2, 3].map(s => (
                            <div key={s} style={{
                              flex: 1, height: 6, borderRadius: 3,
                              background: bookingStep >= s ? '#6366f1' : 'var(--border, #e2e8f0)',
                              transition: 'all 0.4s ease'
                            }} />
                          ))}
                        </div>

                        {/* Step 1: Motif Selection */}
                        {bookingStep === 1 && (
                          <div className="tab-content-anim">
                            <h3 style={{ textAlign: 'center', marginBottom: 32 }}>Quel est l'objet de notre échange ?</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
                              {[
                                { id: 'project', title: 'Point Projet', duration: 30, icon: '🚀', desc: 'Faire avancer mon dossier.' },
                                { id: 'support', title: 'Support Technique', duration: 15, icon: '🛠️', desc: 'Résoudre un point précis.' },
                                { id: 'advice', title: 'Conseil Stratégique', duration: 45, icon: '💡', desc: 'Échanger sur ma vision.' }
                              ].map(motif => (
                                <div
                                  key={motif.id}
                                  className={`booking-card ${selectedReason?.id === motif.id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setSelectedReason(motif);
                                    setBookingStep(2);
                                  }}
                                >
                                  <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>{motif.icon}</div>
                                  <div style={{ fontWeight: 800, fontSize: '1.2rem', marginBottom: 8 }}>{motif.title}</div>
                                  <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>{motif.desc}</p>
                                  <div style={{ marginTop: 16, fontSize: '0.8rem', fontWeight: 700, color: '#6366f1', background: 'rgba(99, 102, 241, 0.1)', padding: '4px 12px', borderRadius: 99, width: 'fit-content', margin: '16px auto 0' }}>
                                    ⏱️ {motif.duration} min
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Step 2: Slot Selection */}
                        {bookingStep === 2 && (
                          <div className="tab-content-anim">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
                              <button className="btn" onClick={() => setBookingStep(1)} style={{ padding: '8px' }}>←</button>
                              <h3 style={{ margin: 0 }}>Sélectionnez votre créneau</h3>
                            </div>

                            {/* Days Sidebar/Selector */}
                            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16, marginBottom: 24, scrollbarWidth: 'none' }}>
                              {bookingSlots.map((day, idx) => (
                                <button
                                  key={idx}
                                  className={`cat-pill ${selectedDayOffset === idx ? 'active' : ''}`}
                                  onClick={() => setSelectedDayOffset(idx)}
                                  style={{ whiteSpace: 'nowrap', minWidth: 100, textAlign: 'center', padding: '12px 20px' }}
                                >
                                  <div style={{ fontSize: '0.75rem', opacity: 0.8, textTransform: 'uppercase' }}>{day.label.split(' ')[0]}</div>
                                  <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{day.label.split(' ')[1]} {day.label.split(' ')[2]}</div>
                                </button>
                              ))}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 12 }}>
                              {bookingSlots[selectedDayOffset].slots.map(slot => (
                                <button
                                  key={slot}
                                  className={`btn ${selectedSlot === slot ? 'success' : ''}`}
                                  style={{
                                    padding: '12px', justifyContent: 'center', fontSize: '1rem', fontWeight: 700,
                                    borderWidth: 2,
                                    borderColor: selectedSlot === slot ? '#10b981' : 'var(--border, #e2e8f0)',
                                    background: selectedSlot === slot ? '#10b981' : 'transparent',
                                    color: selectedSlot === slot ? 'white' : 'inherit'
                                  }}
                                  onClick={() => {
                                    setSelectedSlot(slot);
                                    setBookingStep(3);
                                  }}
                                >
                                  {slot}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Step 3: Confirmation */}
                        {bookingStep === 3 && (
                          <div className="tab-content-anim" style={{ textAlign: 'center', maxWidth: 500, margin: '0 auto' }}>
                            <div style={{ fontSize: '4rem', marginBottom: 24 }}>🗓️</div>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: 16 }}>Confirmer le rendez-vous</h3>
                            <div className="glass-card" style={{ padding: 24, borderRadius: 20, textAlign: 'left', marginBottom: 32 }}>
                              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 }}>
                                <div style={{ fontSize: '2rem' }}>{selectedReason.icon}</div>
                                <div>
                                  <div style={{ fontWeight: 800 }}>{selectedReason.title}</div>
                                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Format : Visio-conférence</div>
                                </div>
                              </div>
                              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                                📅 {bookingSlots[selectedDayOffset].label} à {selectedSlot}
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: 12 }}>
                              <button className="btn" style={{ flex: 1, padding: 14 }} onClick={() => setBookingStep(2)}>Précédent</button>
                              <button
                                className="btn success"
                                style={{ flex: 2, padding: 14, fontWeight: 800 }}
                                onClick={handleBookingSubmit}
                                disabled={isSubmittingBooking}
                              >
                                {isSubmittingBooking ? "Confirmation..." : "Confirmer mon rendez-vous"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        {meetings.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '60px 20px', borderRadius: 24 }} className="premium-empty-state">
                            <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.8 }}>📅</div>
                            <div style={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 8 }} className="empty-title">Aucun rendez-vous planifié</div>
                            <p style={{ fontSize: '0.9rem', opacity: 0.7, margin: 0 }} className="empty-desc">Planifiez un échange avec votre conseiller.</p>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 12 }}>
                            {meetings.map(m => (
                              <div key={m.id} className="meeting-card tab-content-anim" style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: 'var(--surface, #fff)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                  <span style={{
                                    textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 700,
                                    padding: '2px 8px', borderRadius: 999,
                                    background: m.type === 'visio' ? '#e0f2fe' : (m.type === 'call' ? '#f3f4f6' : '#dbeafe'),
                                    color: m.type === 'visio' ? '#0369a1' : (m.type === 'call' ? '#374151' : '#1e40af')
                                  }}>
                                    {m.type === 'visio' ? 'Visio' : (m.type === 'call' ? 'Appel' : 'Rendez-vous')}
                                  </span>
                                  <span style={{ fontSize: '0.85rem', color: 'var(--muted, #6b7280)' }}>
                                    {new Date(m.start).toLocaleDateString()}
                                  </span>
                                </div>
                                <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem' }}>{m.title || 'Rendez-vous'}</h3>
                                <div style={{ fontSize: '0.9rem', color: 'var(--text, #4b5563)', marginBottom: 12 }}>
                                  {new Date(m.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(m.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                {m.notes && (
                                  <div style={{ background: 'var(--bg-subtle, #f9fafb)', padding: '8px 12px', borderRadius: 6, fontSize: '0.85rem', color: 'var(--muted, #6b7280)', maxHeight: 80, overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 12 }} dangerouslySetInnerHTML={{ __html: m.notes }} />
                                )}

                                <div style={{ marginTop: 'auto', paddingTop: 10, borderTop: '1px solid var(--border, #f3f4f6)' }}>
                                  {m.status === 'proposed' ? (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <button
                                        className="btn success"
                                        style={{ flex: 1, justifyContent: 'center', fontSize: '0.9rem' }}
                                        onClick={async () => {
                                          try {
                                            await pb.collection('meetings').update(m.id, { status: 'accepted' });
                                            showToast('Rendez-vous confirmé !', 'success');
                                            setMeetings(prev => prev.map(item => item.id === m.id ? { ...item, status: 'accepted' } : item));
                                          } catch (e) { showToast('Erreur', 'error'); }
                                        }}
                                      >
                                        Accepter
                                      </button>
                                      <button
                                        className="btn"
                                        style={{ flex: 1, justifyContent: 'center', fontSize: '0.9rem', color: '#dc2626', borderColor: '#fee2e2', background: '#fef2f2' }}
                                        onClick={async () => {
                                          if (!window.confirm('Refuser ce créneau ?')) return;
                                          try {
                                            await pb.collection('meetings').update(m.id, { status: 'canceled' });
                                            setMeetings(prev => prev.map(item => item.id === m.id ? { ...item, status: 'canceled' } : item));
                                          } catch (e) { showToast('Erreur', 'error'); }
                                        }}
                                      >
                                        Refuser
                                      </button>
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <span style={{
                                        fontSize: '0.85rem', fontWeight: 600,
                                        color: m.status === 'canceled' ? '#dc2626' : (m.status === 'done' ? '#6b7280' : '#059669')
                                      }}>
                                        {m.status === 'accepted' ? '✅ Accepté' :
                                          (m.status === 'scheduled' ? '📅 Planifié' :
                                            (m.status === 'canceled' ? '❌ Annulé' :
                                              (m.status === 'done' ? 'Terminé' : m.status)))}
                                      </span>
                                      {(m.status === 'accepted' || m.status === 'scheduled') && new Date(m.start) > new Date() && (
                                        <button
                                          style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}
                                          onClick={async () => {
                                            if (!window.confirm('Annuler ce rendez-vous ?')) return;
                                            try {
                                              await pb.collection('meetings').update(m.id, { status: 'canceled' });
                                              setMeetings(prev => prev.map(item => item.id === m.id ? { ...item, status: 'canceled' } : item));
                                            } catch (e) { showToast('Erreur', 'error'); }
                                          }}
                                        >
                                          Annuler
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              }
              {
                activeSection === 'historique' && (
                  <div>
                    <h2 style={{ marginTop: 0 }}>Historique</h2>
                    <p>Historique des actions et documents (à compléter).</p>
                  </div>
                )
              }

              {
                activeSection === 'messages' && (
                  <div className="tab-content-anim">
                    <div className="chat-section" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: 'calc(100vh - 200px)',
                      minHeight: 550,
                      maxHeight: 750,
                      borderRadius: 24,
                      overflow: 'hidden',
                      background: 'var(--card-bg, #fff)',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
                    }}>

                      {/* HEADER DU CHAT (Léon) */}
                      <div className="chat-header">
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                          <div style={{ position: 'relative' }}>
                            <img
                              src="/images/team/pi1.jpg"
                              alt="Léon"
                              onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'grid'; }}
                              style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.1)' }}
                            />
                            <div style={{ display: 'none', width: 48, height: 48, borderRadius: '50%', placeItems: 'center', background: '#3b82f6', color: 'white', fontWeight: 700, border: '2px solid rgba(255,255,255,0.1)' }}>L</div>
                            <div style={{
                              position: 'absolute', bottom: 0, right: 0,
                              width: 12, height: 12, borderRadius: '50%',
                              background: '#10b981', border: '2px solid white'
                            }} className="dark:border-slate-800"></div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <h2 style={{ margin: 0 }}>Léon</h2>
                              <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 99, background: '#dcfce7', color: '#166534', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase' }}>En ligne</span>
                            </div>
                            <div className="chat-header-role">Votre conseiller dédié</div>
                          </div>
                        </div>
                        <div className="chat-header-quote" style={{ marginTop: 12, paddingLeft: 64 }}>
                          "De l'idée à la mise en ligne, je vous accompagne pas à pas pour faire de votre projet un succès."
                        </div>
                      </div>
                      {/* Chat Area */}
                      <div className="chat-messages" style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        background: 'transparent'
                      }} ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
                        {(() => {
                          const groupedMessages = [];
                          let lastDate = null;
                          inboxMessages.forEach(msg => {
                            const dateLabel = getDayLabel(msg.created);
                            if (dateLabel !== lastDate) {
                              groupedMessages.push({ type: 'date', label: dateLabel });
                              lastDate = dateLabel;
                            }
                            groupedMessages.push({ type: 'message', data: msg });
                          });

                          if (inboxMessages.length === 0) return (
                            <div className="premium-empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', borderRadius: 24, padding: 20 }}>
                              <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.8 }}>💬</div>
                              <div style={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: 8 }} className="empty-title">Aucun message pour le moment</div>
                              <p style={{ fontSize: '0.9rem', textAlign: 'center', maxWidth: 300, opacity: 0.7 }} className="empty-desc">Posez vos questions à notre équipe, nous vous répondrons dans les plus brefs délais.</p>
                            </div>
                          );

                          return groupedMessages.map((item, idx) => {
                            if (item.type === 'date') {
                              return (
                                <div key={'date-' + idx} className="chat-date-separator">
                                  <div className="chat-date-line"></div>
                                  {item.label}
                                  <div className="chat-date-line"></div>
                                </div>
                              );
                            }
                            const msg = item.data;
                            const isMe = msg.from === user.id;
                            return (
                              <div key={msg.id} style={{
                                alignSelf: isMe ? 'flex-end' : 'flex-start',
                                maxWidth: '80%',
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 12,
                                alignItems: 'flex-end'
                              }}>
                                {!isMe && (
                                  <div style={{
                                    width: 32, height: 32, borderRadius: 10, background: '#6366f1',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.8rem', color: 'white', fontWeight: 700, flexShrink: 0,
                                    boxShadow: '0 4px 6px -1px rgba(99,102,241,0.2)'
                                  }}>
                                    S
                                  </div>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                                  <div
                                    className={`chat-bubble-${isMe ? 'me' : 'them'}`}
                                    style={{
                                      padding: '12px 16px',
                                      borderRadius: isMe ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                                      position: 'relative'
                                    }}
                                  >
                                    {/* ATTACHMENTS */}
                                    {msg.attachment && (() => {
                                      const files = Array.isArray(msg.attachment) ? msg.attachment : [msg.attachment];
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: msg.content ? 10 : 0 }}>
                                          {files.map((filename, filIdx) => {
                                            const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(filename);
                                            return (
                                              <div key={filIdx}>
                                                {isImg ? (
                                                  <a href={pb.files.getUrl(msg, filename)} target="_blank" rel="noreferrer">
                                                    <img src={pb.files.getUrl(msg, filename)} alt="attachment"
                                                      style={{ maxWidth: '100%', borderRadius: 8, display: 'block', maxHeight: 200, objectFit: 'cover' }}
                                                    />
                                                  </a>
                                                ) : (
                                                  <a href={pb.files.getUrl(msg, filename)} target="_blank" rel="noreferrer"
                                                    style={{
                                                      display: 'inline-flex', alignItems: 'center', gap: 8,
                                                      padding: '8px 12px', borderRadius: 10,
                                                      color: isMe ? 'white' : '#6366f1',
                                                      background: isMe ? 'rgba(255,255,255,0.1)' : '#f5f7ff',
                                                      textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem'
                                                    }}>
                                                    <span>📎</span>
                                                    <span style={{ textDecoration: 'underline' }}>{filename}</span>
                                                  </a>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.95rem' }}>{msg.content}</div>
                                  </div>
                                  <div style={{
                                    fontSize: '0.65rem',
                                    color: '#94a3b8',
                                    marginTop: 4,
                                    display: 'flex',
                                    gap: 4,
                                    alignItems: 'center',
                                    fontWeight: 700,
                                    textTransform: 'uppercase'
                                  }}>
                                    {new Date(msg.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    {isMe && <span>{msg.read ? '• Distribué' : '• Envoyé'}</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>

                      {/* Input Area */}
                      <div className="chat-input-area">
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          const form = e.target;
                          const content = form.content.value.trim();
                          if (!content && msgAttachments.length === 0) return;

                          const formData = new FormData();
                          formData.append('subject', 'Message client');
                          formData.append('content', content);
                          formData.append('from', user.id);
                          if (customer?.created_by) formData.append('to', customer.created_by);
                          if (customer?.id) formData.append('customer_id', customer.id);
                          formData.append('read', 'false');

                          msgAttachments.forEach(file => {
                            formData.append('attachment', file);
                          });

                          try {
                            await pb.collection('messages').create(formData);
                            form.reset();
                            setMsgAttachments([]); // Clear attachments
                            // Update realtime fetch
                            const filter = `to = "${user.id}" || from = "${user.id}"`;
                            const mRes = await pb.collection('messages').getList(1, 50, { filter, sort: 'created', expand: 'from,to' });
                            setInboxMessages(mRes?.items || []);
                          } catch (err) { alert('Erreur envoi'); }
                        }}>
                          {/* PREVIEW ATTACHMENTS */}
                          {msgAttachments.length > 0 && (
                            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid #f8fafc' }}>
                              {msgAttachments.map((f, i) => (
                                <div key={i} style={{
                                  padding: '6px 12px', borderRadius: 10, fontSize: '0.8rem',
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  background: '#f1f5f9', color: '#475569', fontWeight: 600,
                                  border: '1px solid #e2e8f0'
                                }} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300">
                                  <span style={{ display: 'flex', alignItems: 'center' }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                    </svg>
                                    <span style={{ marginLeft: 6 }}>{f.name}</span>
                                  </span>
                                  <button type="button" onClick={() => setMsgAttachments(prev => prev.filter((_, idx) => idx !== i))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.2rem', padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="chat-input-wrap" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', padding: '6px 12px', borderRadius: 24 }}>
                            <button
                              type="button"
                              onClick={() => msgFileInputRef.current.click()}
                              style={{
                                border: 'none', background: 'var(--card-bg, #fff)',
                                width: 36, height: 36, borderRadius: 10,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', flexShrink: 0, fontSize: '1.2rem',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                color: '#64748b'
                              }}
                              className="dark:bg-slate-700 dark:text-slate-300"
                              title="Joindre des fichiers"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                              </svg>
                            </button>
                            <input
                              type="file"
                              multiple
                              ref={msgFileInputRef}
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files) {
                                  const newFiles = Array.from(e.target.files);
                                  setMsgAttachments(prev => [...prev, ...newFiles].slice(0, 5));
                                }
                              }}
                            />
                            <textarea
                              name="content"
                              rows="1"
                              placeholder="Écrivez votre message..."
                              style={{
                                flex: 1, border: 'none', background: 'transparent',
                                outline: 'none', fontSize: '0.95rem', resize: 'none',
                                padding: '10px 4px', color: 'inherit',
                                lineHeight: '1.5', fontFamily: 'Inter, sans-serif',
                                maxHeight: '120px', overflowY: 'auto'
                              }}
                              onInput={(e) => {
                                e.target.style.height = 'auto'; // Reset height
                                e.target.style.height = e.target.scrollHeight + 'px'; // Set new height
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  e.target.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                                  e.target.style.height = 'auto'; // Reset height after submit
                                }
                              }}
                            />
                            <button
                              type="submit"
                              style={{
                                background: '#6366f1', color: 'white', border: 'none',
                                width: 36, height: 36, borderRadius: 10,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', flexShrink: 0,
                                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'translateX(1px)' }}>
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 1 9 22 2" />
                              </svg>
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )
              }
              {
                activeSection === 'contact' && (
                  <div style={{ padding: 20, textAlign: 'center', color: 'gray' }}>
                    (Redirection vers Messagerie...)
                  </div>
                )
              }
            </div >

            {
              user?.role === 'admin' && (
                <div style={{ marginTop: 24 }}>
                  <h2>Espace administrateur</h2>
                  <p>Accès réservé.</p>
                  <Link to="/admin">Aller à l'admin</Link>
                </div>
              )
            }
            {/* Help bar styled like onboarding footer - Hidden on Desktop because of Sticky Sidebar */}
            <div style={{ marginTop: 24 }} className="mobile-dock-only">
              <ClientHelpBar />
            </div>

            {/* Global Modal: Validation devis (visible on any tab) */}
            {
              selectedQuote && (
                <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setSelectedQuote(null)}>
                  <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Validation du devis</h3>
                      <button className="close-btn" onClick={() => setSelectedQuote(null)}>✕</button>
                    </div>
                    <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '24px' }}>

                      {/* Payment Options */}
                      <div style={{
                        padding: 16,
                        borderRadius: 10,
                        border: '1px solid var(--border, #e5e7eb)',
                        backgroundColor: 'var(--surface-alt, rgba(125,125,125,0.03))'
                      }}>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 12, color: 'var(--text, inherit)' }}>
                          Mode de paiement souhaité
                        </label>
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                          {selectedQuote.payment_config?.allow_full !== false && (
                            <div onClick={() => setPaymentChoice('full')} style={{
                              flex: 1,
                              border: paymentChoice === 'full' ? '2px solid #10b981' : '1px solid var(--border, #d1d5db)',
                              padding: '12px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: paymentChoice === 'full' ? 'rgba(16, 185, 129, 0.08)' : 'var(--surface, rgba(255,255,255,0.5))',
                              minWidth: '120px',
                              transition: 'all 0.2s ease'
                            }}>
                              <strong style={{ display: 'block', color: paymentChoice === 'full' ? '#10b981' : 'var(--text, inherit)' }}>Comptant</strong>
                              <div style={{ fontSize: '.8rem', color: 'var(--muted, #6b7280)' }}>100% à la commande</div>
                            </div>
                          )}
                          {selectedQuote.payment_config?.allow_half !== false && (
                            <div onClick={() => setPaymentChoice('half')} style={{
                              flex: 1,
                              border: paymentChoice === 'half' ? '2px solid #10b981' : '1px solid var(--border, #d1d5db)',
                              padding: '12px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: paymentChoice === 'half' ? 'rgba(16, 185, 129, 0.08)' : 'var(--surface, rgba(255,255,255,0.5))',
                              minWidth: '120px',
                              transition: 'all 0.2s ease'
                            }}>
                              <strong style={{ display: 'block', color: paymentChoice === 'half' ? '#10b981' : 'var(--text, inherit)' }}>50/50</strong>
                              <div style={{ fontSize: '.8rem', color: 'var(--muted, #6b7280)' }}>50% Acompte</div>
                            </div>
                          )}
                          {selectedQuote.payment_config?.allow_installments !== false && (
                            <div onClick={() => setPaymentChoice('installments')} style={{
                              flex: 1,
                              border: paymentChoice === 'installments' ? '2px solid #10b981' : '1px solid var(--border, #d1d5db)',
                              padding: '12px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: paymentChoice === 'installments' ? 'rgba(16, 185, 129, 0.08)' : 'var(--surface, rgba(255,255,255,0.5))',
                              minWidth: '120px',
                              transition: 'all 0.2s ease'
                            }}>
                              <strong style={{ display: 'block', color: paymentChoice === 'installments' ? '#10b981' : 'var(--text, inherit)' }}>Échelonné</strong>
                              <div style={{ fontSize: '.8rem', color: 'var(--muted, #6b7280)' }}>Mensualités</div>
                            </div>
                          )}
                        </div>

                        {paymentChoice === 'installments' && (
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border, #e5e7eb)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                              <label style={{ fontSize: '.9rem', whiteSpace: 'nowrap', color: 'var(--text, inherit)' }}>Nombre de mensualités :</label>
                              <select
                                value={installmentsCount}
                                onChange={(e) => setInstallmentsCount(Number(e.target.value))}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: 6,
                                  border: '1px solid var(--border, #d1d5db)',
                                  background: 'var(--surface, #fff)',
                                  color: 'var(--text, #000)',
                                  cursor: 'pointer',
                                  outline: 'none'
                                }}
                              >
                                {Array.from({ length: (selectedQuote.payment_config?.installments_max_count || 12) - 1 }, (_, i) => i + 2).map(n => (
                                  <option key={n} value={n}>{n} fois</option>
                                ))}
                              </select>
                            </div>
                            <div style={{ fontSize: '.85rem', color: 'var(--muted, #6b7280)', marginTop: 8, fontStyle: 'italic', background: 'var(--surface, #fff)', padding: '8px', borderRadius: 4, display: 'inline-block' }}>
                              {(() => {
                                const pct = selectedQuote.payment_config?.installments_deposit_percent ?? 20;
                                const factor = pct / 100;
                                const remFactor = 1 - factor;
                                return `Acompte immédiat de ${((selectedQuote.total || 0) * factor).toFixed(2)}€ (${pct}%), puis ${installmentsCount - 1} mensualités de ~${Number((((selectedQuote.total || 0) * remFactor) / Math.max(1, installmentsCount - 1)).toFixed(2))}€`;
                              })()}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 2. Contract Preview - Clean Full Width */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label style={{ fontWeight: 600, color: 'var(--text, inherit)', fontSize: '0.95rem' }}>
                            Prévisualisation du contrat à signer
                          </label>
                          <button
                            onClick={() => setFullScreenContract(true)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--primary, #6366f1)',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: '500'
                            }}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                            Plein écran
                          </button>
                        </div>

                        <div
                          style={{
                            height: '380px',
                            overflowY: 'auto',
                            border: '1px solid var(--border, #e5e7eb)',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '0',
                            position: 'relative'
                          }}
                          className="contract-scroll-area"
                        >
                          <div
                            style={{
                              color: '#000',
                              background: '#fff',
                              minHeight: '100%',
                              padding: '16px'
                            }}
                            dangerouslySetInnerHTML={{
                              __html: generateContractHtml(
                                selectedQuote,
                                customer || { name: user?.name, email: user?.email },
                                calculatePaymentPlan(selectedQuote, paymentChoice, installmentsCount),
                                selectedQuote.total || 0,
                                sigLocation, // Pass location
                                sigName // Pass signature text
                              )
                            }}
                          />
                        </div>
                      </div>

                      {/* Full Screen Contract Overlay */}
                      {fullScreenContract && (
                        <div style={{
                          position: 'fixed',
                          inset: 0,
                          zIndex: 10060, // Higher than modal
                          background: 'rgba(0,0,0,0.8)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '20px',
                          backdropFilter: 'blur(5px)'
                        }}>
                          <div style={{
                            background: '#fff',
                            width: '100%',
                            maxWidth: '900px',
                            height: '90vh',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '16px 24px',
                              borderBottom: '1px solid #e5e7eb',
                              background: '#f9fafb',
                              borderTopLeftRadius: '8px',
                              borderTopRightRadius: '8px'
                            }}>
                              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#111827' }}>Lecture du Contrat</h3>
                              <button
                                onClick={() => setFullScreenContract(false)}
                                style={{
                                  background: '#fff',
                                  border: '1px solid #d1d5db',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  color: '#374151',
                                  transition: 'all 0.2s'
                                }}
                              >
                                Fermer
                              </button>
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto', padding: '40px', color: '#000' }}>
                              <div dangerouslySetInnerHTML={{
                                __html: generateContractHtml(
                                  selectedQuote,
                                  customer || { name: user?.name, email: user?.email },
                                  calculatePaymentPlan(selectedQuote, paymentChoice, installmentsCount),
                                  selectedQuote.total || 0,
                                  sigLocation, // Pass location
                                  sigName // Pass signature text
                                )
                              }} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Controls Section*/}
                      <div style={{ display: 'grid', gap: 20 }}>

                        {/* Signature Zone */}
                        <div>
                          <div style={{ fontSize: '1.2em', textAlign: 'right', marginBottom: 16 }}>
                            Total à payer : <strong style={{ color: 'var(--text, inherit)' }}>{typeof selectedQuote.total === 'number' ? selectedQuote.total.toFixed(2) : '0.00'}€ TTC</strong>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>

                            {/* City Input */}
                            <div>
                              <label style={{ display: 'block', fontSize: '0.9em', marginBottom: 6, color: 'var(--text, inherit)' }}>
                                Fait à (Ville de signature) :
                              </label>
                              <input
                                type="text"
                                value={sigLocation}
                                onChange={(e) => setSigLocation(e.target.value)}
                                placeholder='Votre ville (ex: Paris)'
                                style={{
                                  width: '100%',
                                  padding: '12px',
                                  fontSize: '1rem',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border, #d1d5db)',
                                  background: 'var(--surface, #fff)',
                                  color: 'var(--text, #111827)'
                                }}
                              />
                            </div>

                            <div>
                              <label style={{ display: 'block', fontSize: '0.9em', marginBottom: 6, color: 'var(--text, inherit)' }}>
                                Pour signer le contrat, écrivez <em>"Lu et approuvé"</em> suivi de votre nom :
                              </label>
                              <input
                                type="text"
                                value={sigName}
                                onChange={(e) => setSigName(e.target.value)}
                                placeholder='Exemple : Lu et approuvé Jean Dupont'
                                style={{
                                  width: '100%',
                                  padding: '12px',
                                  fontSize: '1rem',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border, #d1d5db)',
                                  background: 'var(--surface, #fff)',
                                  color: 'var(--text, #111827)'
                                }}
                              />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 0', color: 'var(--text, inherit)' }}>
                              <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '0.95rem', lineHeight: '1.4' }}>
                                Je reconnais avoir pris connaissance des détails du devis, du contrat ci-dessus et des <a href="/cgv" target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', color: 'var(--primary, #6366f1)' }}>Conditions Générales de Vente</a>.
                              </span>
                            </label>
                          </div>

                          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 12, borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 16 }}>
                            <button className="btn" onClick={() => setSelectedQuote(null)} style={{ padding: '10px 24px', background: 'transparent', color: 'var(--text, inherit)' }}>Annuler</button>
                            <button
                              className="btn success"
                              disabled={!consent || !sigName || sigName.length < 5 || !sigLocation || workingQuote}
                              onClick={approveSelectedQuote}
                              style={{
                                background: (!consent || !sigName || sigName.length < 5 || !sigLocation) ? 'var(--disabled, #9ca3af)' : 'var(--primary, #10b981)',
                                color: '#fff',
                                border: 'none',
                                padding: '12px 32px',
                                fontWeight: '600',
                                borderRadius: '8px',
                                cursor: (!consent || !sigName || sigName.length < 5 || !sigLocation) ? 'not-allowed' : 'pointer',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                              }}
                            >
                              {workingQuote ? 'Traitement en cours...' : 'Signer le contrat et Valider'}
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                  <style>{`
            .modal-overlay { position: fixed; inset:0; background: rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:10050; padding:20px; backdrop-filter: blur(4px); }
            .modal-content { 
              background: var(--surface, #fff); 
              color: var(--text, #111827); 
              border: 1px solid var(--border, #e5e7eb); 
              border-radius: 12px; 
              width: 100%; 
              max-width: 760px; 
              max-height: 90vh; 
              overflow-y: auto; 
              display: flex; 
              flex-direction: column;
              box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04); 
            }
            .dark .modal-content {
              background: var(--surface, #0b1220);
              border-color: var(--border, #1f2937);
              box-shadow: 0 20px 25px -5px rgba(0,0,0,0.5); 
            }
            .modal-header { display:flex; justify-content:space-between; align-items:center; padding:20px 24px; border-bottom:1px solid var(--border, #e5e7eb); }
            .modal-body { padding: 24px; overflow-y: auto; }
            .close-btn { background:none; border:1px solid transparent; color: var(--muted, #9ca3af); cursor:pointer; padding:8px; border-radius:6px; transition: all 0.2s; }
            .close-btn:hover { background: rgba(0,0,0,0.05); color: var(--text, #111827); }
            .dark .close-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }

            /* Scrollbar styling for modal body */
            .modal-body::-webkit-scrollbar { width: 8px; }
            .modal-body::-webkit-scrollbar-track { background: transparent; }
            .modal-body::-webkit-scrollbar-thumb { background-color: var(--border, #d1d5db); border-radius: 20px; border: 3px solid transparent; background-clip: content-box; }
            .contract-scroll-area::-webkit-scrollbar { width: 8px; }
            .contract-scroll-area::-webkit-scrollbar-track { background: transparent; }
            .contract-scroll-area::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
          `}</style>
                </div>
              )
            }


            {/* Help Section Footer - ClientHelpBar Style */}
            <div className="help-footer-section" style={{
              marginTop: 60,
              padding: '24px 0',
              borderTop: '1px solid var(--border, #e5e7eb)',
              textAlign: 'center',
              color: 'var(--muted, #6b7280)'
            }}>
              <div className="help-flex-container" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                marginBottom: 32,
                cursor: 'default'
              }}>
                {/* Team Icon */}
                <div className="team-icon-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                </div>

                <div className="help-text-content" style={{ textAlign: 'left', maxWidth: 360 }}>
                  <h3 className="help-title" style={{
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    marginBottom: 6,
                    marginTop: 0,
                    color: 'var(--text-primary, #1e293b)',
                    textTransform: 'none',
                    letterSpacing: '-0.01em'
                  }}>Besoin d’aide ?</h3>

                  <p style={{
                    fontSize: '1.05rem',
                    color: 'var(--muted, #64748b)',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    Une question sur votre projet ? <strong style={{ color: 'var(--primary, #6366f1)' }}>Nous sommes</strong> à votre écoute pour vous accompagner à chaque étape, de la conception à la mise en ligne.
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 20,
                flexWrap: 'wrap',
                marginBottom: 24
              }}>
                <a href="mailto:contact@infocolab.com" className="help-contact-btn">
                  <div className="icon-box email-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                  </div>
                  <span>contact@infocolab.com</span>
                </a>
                <a href="tel:+33769297963" className="help-contact-btn">
                  <div className="icon-box phone-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                  </div>
                  <span>07 69 29 79 63</span>
                </a>
              </div>

              <div style={{ fontSize: '0.9rem' }}>
                <span style={{ fontWeight: 700, color: 'var(--text-primary, #1e293b)' }}>À votre écoute</span> <span style={{ opacity: 0.6 }}>•</span> Un conseiller vous répond rapidement
              </div>

              <style>{`
                .help-contact-btn {
                    display: flex; align-items: center; gap: 10px;
                    padding: 8px 16px;
                    background: var(--surface, #ffffff);
                    border: 1px solid var(--border, #e2e8f0);
                    border-radius: 10px;
                    text-decoration: none;
                    color: var(--primary, #6366f1);
                    font-weight: 700;
                    font-size: 0.95rem;
                    transition: all 0.2s ease;
                }
                .help-contact-btn:hover {
                    background: var(--surface-alt, #f8fafc);
                    border-color: var(--primary, #6366f1);
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }
                .help-contact-btn .icon-box {
                    width: 32px; height: 32px;
                    border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                }
                .help-contact-btn .email-icon { background: rgba(99, 102, 241, 0.1); color: #6366f1; }
                .help-contact-btn .phone-icon { background: rgba(16, 185, 129, 0.1); color: #10b981; }

                .help-title {
                    color: var(--text-primary, #1e293b);
                    transition: color 0.2s ease;
                }
                .help-footer-section:hover .help-title {
                    color: var(--primary, #6366f1);
                }
                
                .team-icon-wrap {
                    width: 64px; height: 64px;
                    border-radius: 50%;
                    background: var(--surface, #ffffff);
                    border: 1px solid var(--border, #e2e8f0);
                    display: flex;
                    alignItems: center;
                    justify-content: center;
                    color: var(--primary, #6366f1);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    flex-shrink: 0;
                    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
                .help-footer-section:hover .team-icon-wrap {
                    transform: scale(1.1) rotate(5deg);
                    background: var(--primary, #6366f1);
                    color: white;
                    border-color: var(--primary, #6366f1);
                    box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3);
                }

                .help-contact-btn {
                    display: flex; align-items: center; gap: 10px;
                    padding: 8px 16px;
                    background: var(--surface, #ffffff);
                    border: 1px solid var(--border, #e2e8f0);
                    border-radius: 10px;
                    text-decoration: none;
                    color: var(--primary, #6366f1);
                    font-weight: 700;
                    font-size: 0.95rem;
                    transition: all 0.2s ease;
                }
                .help-contact-btn:hover {
                    background: var(--surface-alt, #f8fafc);
                    border-color: var(--primary, #6366f1);
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }
                .help-contact-btn .icon-box {
                    width: 32px; height: 32px;
                    border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                    transition: all 0.2s ease;
                }
                .help-contact-btn .email-icon { background: rgba(99, 102, 241, 0.1); color: #6366f1; }
                .help-contact-btn .phone-icon { background: rgba(16, 185, 129, 0.1); color: #10b981; }
                
                .help-contact-btn:hover .icon-box {
                    transform: scale(1.1);
                }

                .dark .help-contact-btn {
                    background: var(--card-bg, #1e293b);
                    border-color: var(--border, #334155);
                    color: var(--primary, #818cf8);
                }
                .dark .help-contact-btn:hover {
                    background: var(--surface, #0f172a);
                    border-color: var(--primary, #818cf8);
                }
                .dark .help-footer-section {
                    color: var(--muted, #94a3b8);
                    border-color: var(--border, #334155);
                }
                .dark .help-title {
                    color: #ffffff !important;
                }
                .dark .team-icon-wrap {
                    background: #1e293b;
                    border-color: #334155;
                }
                .dark span[style*="var(--text-primary"] {
                    color: #f1f5f9 !important;
                }

                @media (max-width: 600px) {
                    .help-footer-section .help-flex-container {
                        flex-direction: column;
                        text-align: center !important;
                    }
                    .help-footer-section .help-text-content {
                        text-align: center !important;
                    }
                }
              `}</style>
            </div>

          </div>

          <div className="advisor-sidebar">
            <div className="advisor-sticky-wrapper" style={{ background: 'transparent', border: 'none', boxShadow: 'none', overflow: 'visible' }}>
              {/* Advisor / Pin Card (Desktop Only) - Restored Glass Style */}
              <div className="card glass-card desktop-sidebar-only" style={{ borderRadius: 24, padding: 24, textAlign: 'center', border: '1px solid rgba(16, 185, 129, 0.2)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'relative', width: 100, height: 100, margin: '0 auto 16px' }}>
                  <img
                    src={advisor.avatar}
                    alt={advisor.name}
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: '3px solid white', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}
                  />
                  <div style={{ position: 'absolute', bottom: 5, right: 5, width: 24, height: 24, background: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <div className="presence-pulse"></div>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div className="advisor-name" style={{ fontWeight: 800, fontSize: '1.25rem', color: '#111827' }}>{advisor.name}</div>
                  <div style={{ fontSize: '0.85rem', color: '#6366f1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{advisor.role}</div>
                </div>

                <div className="advisor-bio" style={{ background: 'rgba(248, 250, 252, 0.5)', borderRadius: 16, padding: 16, fontSize: '0.85rem', color: '#475569', lineHeight: 1.5, marginBottom: 24, border: '1px solid rgba(226, 232, 240, 0.6)' }}>
                  "{advisor.bio}"
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  <button
                    onClick={() => navigate('/mon-compte/messages')}
                    className="btn success"
                    style={{ width: '100%', justifyContent: 'center', padding: '12px', borderRadius: 14, border: 'none', fontWeight: 700, boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                    Chatter avec {advisor.name}
                  </button>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <a href="mailto:contact@infocolab.com" className="btn" style={{ flex: 1, justifyContent: 'center', borderRadius: 12 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                    </a>
                    <a href="tel:+33769297963" className="btn" style={{ flex: 1, justifyContent: 'center', borderRadius: 12 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Booking Card */}
              <div className="card" style={{ marginTop: 20, borderRadius: 24, padding: 24, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', border: 'none', boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>📅</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Planifier un échange</div>
                </div>
                <p style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: 20, lineHeight: 1.5 }}>
                  Besoin de faire le point ? Réservez un créneau de 15 min avec votre conseiller.
                </p>
                <button
                  onClick={() => navigate('/mon-compte/rendez-vous')}
                  className="btn"
                  style={{ width: '100%', justifyContent: 'center', background: 'white', color: '#4f46e5', border: 'none', borderRadius: 14, fontWeight: 700, padding: 12 }}
                >
                  Voir les disponibilités
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DashboardFooter profile={profile} showToast={showToast} />


      {showWelcomeOverlay && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10001,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)',
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <div style={{
            textAlign: 'center', maxWidth: 500, width: '90%', padding: '50px 30px',
            background: 'var(--surface, #ffffff)', color: 'var(--text, #1e293b)',
            borderRadius: 32, boxShadow: '0 25px 70px -15px rgba(0,0,0,0.4)',
            border: '1px solid var(--border, #e2e8f0)',
            position: 'relative', overflow: 'hidden',
            animation: 'popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}>
            <div style={{ fontSize: '4.5rem', marginBottom: 20 }}>👋</div>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 900, margin: '0 0 10px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Ravi de vous revoir !
            </h1>
            <p style={{ fontSize: '1.25rem', opacity: 0.9, marginBottom: 30, fontWeight: 500 }}>
              Heureux de vous retrouver, {user?.name || 'cher client'}.<br />Votre espace est prêt.
            </p>

            <button
              onClick={() => setShowWelcomeOverlay(false)}
              className="btn success"
              style={{ padding: '16px 40px', fontSize: '1.1rem', borderRadius: 16, border: 'none', fontWeight: 800, width: '100%', justifyContent: 'center', boxShadow: '0 10px 20px -5px rgba(16, 185, 129, 0.4)' }}
            >
              C'est parti ! 🚀
            </button>

            <button
              onClick={() => setShowWelcomeOverlay(false)}
              style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, fontSize: 20, color: 'inherit' }}
            >✕</button>
          </div>
        </div>
      )}

      {/* Premium UI: Toast UI */}
      {toast.show && (
        <div style={{
          position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)',
          zIndex: 20000, padding: '14px 28px', borderRadius: 16,
          background: toast.type === 'success' ? '#10b981' : toast.type === 'error' ? '#ef4444' : '#3b82f6',
          color: 'white', fontWeight: 600, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: 12,
          animation: 'toastIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards'
        }}>
          {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'} {toast.message}
          <style>{`
            @keyframes toastIn {
              from { opacity: 0; transform: translate(-50%, 40px) scale(0.9); }
              to { opacity: 1; transform: translate(-50%, 0) scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* "Léon Bubble" (Floating FAB) - Universal */}
      <div className="leon-fab" onClick={() => setShowLeonModal(!showLeonModal)} style={{
        position: 'fixed', right: 26, zIndex: 9990,
        width: 60, height: 60, borderRadius: '50%',
        boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
        cursor: 'pointer', border: '3px solid white',
        display: 'block', // Visible on Desktop too
        bottom: 20, // Default Desktop bottom
        transition: 'bottom 0.3s ease'
      }}>
        <img src={advisor.avatar} alt="Léon" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
        <div style={{
          position: 'absolute', bottom: 2, right: 2, width: 14, height: 14,
          background: '#10b981', borderRadius: '50%', border: '2px solid white'
        }}></div>
      </div>

      {/* Léon Chat Popup (Universal Floating Style) */}
      {showLeonModal && (
        <div className="leon-chat-popup" style={{
          position: 'fixed', bottom: 90, right: 30, zIndex: 10000,
          width: 380, height: 600, maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          pointerEvents: 'none' // Allows clicking through outside the box
        }}>
          <div className="leon-sheet-content" style={{
            background: 'var(--card-bg, #fff)',
            width: '100%', height: '100%',
            borderRadius: 24,
            boxShadow: '0 5px 40px rgba(0,0,0,0.16)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            animation: 'popIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            transformOrigin: 'bottom right',
            pointerEvents: 'auto', // Re-enable clicks inside
            border: '1px solid var(--border-color, rgba(0,0,0,0.05))'
          }}>
            {/* Modal Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color, #e2e8f0)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ position: 'relative' }}>
                  <img src={advisor.avatar} alt="Léon" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, background: '#10b981', borderRadius: '50%', border: '2px solid white' }}></div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary, #111827)' }}>{advisor.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6366f1' }}>En ligne</div>
                </div>
              </div>
              <button onClick={() => setShowLeonModal(false)} style={{ background: 'transparent', border: 'none', padding: 8 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            {/* Modal Chat Content */}
            <div style={{ flex: 1, padding: 16, overflowY: 'auto', background: 'var(--bg-secondary, #f8fafc)' }} className="mobile-chat-body">
              {inboxMessages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                  Dites bonjour à Léon ! 👋
                </div>
              ) : (
                inboxMessages.map(msg => {
                  const isMe = msg.from === user.id;
                  return (
                    <div key={msg.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                      {/* Avatar for Them */}
                      {!isMe && (
                        <div style={{ width: 28, height: 28, borderRadius: '50%', marginRight: 8, overflow: 'hidden' }}>
                          <img src={advisor.avatar} alt="Adv" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}
                      <div style={{
                        padding: '10px 14px',
                        borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        maxWidth: '75%',
                        background: isMe ? '#6366f1' : 'var(--bubble-incoming-bg, white)',
                        color: isMe ? 'white' : 'var(--text-primary, #1e293b)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                        fontSize: '0.9rem',
                        border: isMe ? 'none' : '1px solid var(--border-color, #e2e8f0)'
                      }}>
                        {msg.content}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Input */}
            <div style={{ padding: 16, borderTop: '1px solid var(--border-color, #e2e8f0)', background: 'var(--card-bg, #fff)' }}>
              <form onSubmit={(e) => {
                e.preventDefault();
                const val = e.target.elements.mobileContent.value.trim();
                if (!val) return;

                // Optimistic add + API call
                const tempMsg = { id: 'temp-' + Date.now(), content: val, from: user.id, created: new Date().toISOString() };
                setInboxMessages(prev => [...prev, tempMsg]);
                e.target.reset();

                pb.collection('messages').create({
                  content: val,
                  from: user.id,
                  to: 'admin_user', // Simplified
                  has_read: false
                }).then(res => {
                  setInboxMessages(prev => prev.map(m => m.id === tempMsg.id ? res : m));
                }).catch(err => {
                  console.error("Message send failed", err);
                });

              }} style={{ display: 'flex', gap: 10 }}>
                <input name="mobileContent" placeholder="Écrivez un message..." style={{ flex: 1, padding: '12px 16px', borderRadius: 24, border: '1px solid var(--border-color, #e2e8f0)', outline: 'none', background: 'var(--input-bg, #f1f5f9)', color: 'var(--text-primary, #111827)', fontSize: '1rem' }} autoComplete="off" />
                <button type="submit" style={{ width: 48, height: 48, borderRadius: '50%', background: '#6366f1', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(99,102,241,0.3)' }}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 1 9 22 2"></polygon></svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes popIn { from { transform: scale(0.8) translateY(20px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }
      @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      
      @media (max-width: 900px) {
        .mobile-only { display: block !important; }
        .leon-fab { bottom: calc(85px + env(safe-area-inset-bottom)) !important; right: 16px !important; }
        .leon-sheet-content { margin-bottom: 80px !important; margin-right: -10px !important; }
      }

      /* Premium Empty State Styles */
      .premium-empty-state {
        background: #f8fafc;
        border: 2px dashed #e2e8f0;
        color: #64748b;
      }
      .empty-title { color: #475569; }
      
      .dark .premium-empty-state {
        background: #0f172a !important; /* Slate 900 */
        border: 1px solid rgba(99, 102, 241, 0.4) !important; /* Violet Border */
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        color: #94a3b8;
      }
      .dark .empty-title { color: #e2e8f0; }
      .dark .empty-desc { color: #94a3b8; }

      /* Premium Nav Card */
      .premium-nav-card {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 24px;
        border-radius: 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        text-decoration: none;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
        min-height: 160px;
      }
      .premium-nav-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.1);
        border-color: #6366f1;
      }
      .nav-card-icon { font-size: 2rem; margin-bottom: 16px; transition: transform 0.3s; }
      .premium-nav-card:hover .nav-card-icon { transform: scale(1.1); }
      
      .premium-nav-title { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
      .premium-nav-desc { font-size: 0.9rem; color: #64748b; line-height: 1.4; }
      .premium-nav-arrow { 
        position: absolute; bottom: 20px; right: 20px; 
        font-size: 1.2rem; color: #cbd5e1; opacity: 0; transform: translateX(-10px); 
        transition: all 0.3s; 
      }
      .premium-nav-card:hover .premium-nav-arrow { opacity: 1; transform: translateX(0); color: #6366f1; }

      /* Dark Mode Nav Card */
      .dark .premium-nav-card {
        background: #0f172a !important;
        border: 1px solid rgba(99, 102, 241, 0.4) !important;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      }
      .dark .premium-nav-title { color: #f8fafc; }
      .dark .premium-nav-desc { color: #94a3b8; }
      .dark .premium-nav-card:hover {
        border-color: rgba(99, 102, 241, 0.8) !important;
        background: #151e32 !important;
        box-shadow: 0 10px 30px rgba(99, 102, 241, 0.15);
      }

      /* Premium Dark Chat Style */
      .dark .leon-chat-popup {
          --card-bg: #0f172a;
          --bg-secondary: #020617; /* Chat area background */
          --border-color: rgba(99, 102, 241, 0.4);
          --text-primary: #f8fafc;
          --bubble-incoming-bg: #1e293b;
          --input-bg: #1e293b;
      }
      .dark .premium-action-card:hover {
        background: #151e32 !important;
        border-color: rgba(99, 102, 241, 0.6) !important;
      }

      /* Premium Stat Card */
      .premium-stat-card {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 16px;
        text-decoration: none;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        color: inherit;
      }
      .premium-stat-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.1);
        border-color: var(--accent-color, #3b82f6);
      }
      .stat-icon-box {
        width: 48px; height: 48px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 12px;
        transition: transform 0.3s;
      }
      .premium-stat-card:hover .stat-icon-box { transform: scale(1.1); }
      .stat-label { font-size: 0.85rem; font-weight: 600; color: var(--muted, #64748b); text-transform: uppercase; letter-spacing: 0.05em; }
      .stat-value { font-size: 2rem; font-weight: 800; color: var(--text-primary, #1e293b); line-height: 1; margin-top: 4px; }
      .stat-arrow { margin-left: auto; color: #cbd5e1; font-size: 1.2rem; transition: all 0.3s; opacity: 0; transform: translateX(-10px); }
      .premium-stat-card:hover .stat-arrow { opacity: 1; transform: translateX(0); color: var(--accent-color, #3b82f6); }

      /* Dark Mode Stat Card */
      .dark .premium-stat-card {
        background: #0f172a !important;
        border: 1px solid rgba(99, 102, 241, 0.4) !important;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      }
      .dark .stat-value { color: #f8fafc; }
      .dark .stat-label { color: #94a3b8; }
      .dark .premium-stat-card:hover { border-color: rgba(99, 102, 241, 0.8) !important; }

      .dark .leon-sheet-content {
            box-shadow: 0 10px 40px rgba(0,0,0,0.5) !important;
      }

      /* Mobile Chat: Bottom Sheet Style */
      @media (max-width: 768px) {
        .leon-chat-popup {
          bottom: 0 !important;
          right: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 85vh !important;
          max-height: 85vh !important;
          border-radius: 24px 24px 0 0 !important;
          margin: 0 !important;
        }
        .leon-sheet-content {
          border-radius: 24px 24px 0 0 !important;
          height: 100% !important;
          border-bottom: none !important;
        }
        /* Adjust FAB to not overlap or hide it */
        .leon-fab {
            bottom: 20px !important;
            right: 20px !important;
        }
        /* Hide FAB when chat is open? Optional, handled by React conditional usually, but here chat is conditionally rendered */
      }
    `}</style>
    </div>
  );
}

export default DashboardPage;
